import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "@components/Layout";
import { HelionButton } from "@components/ui/HelionButton";
import { Input } from "@components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { API_BASE_URL } from "@lib/config";
import { fmtLocalDate, fmtLocalDateTime } from "@lib/dateUtils";
// ONE mapping of the disposition's `harvestLock` block to words, so a lockout that failed
// to start can never render as a success. See lib/harvestLockout.ts.
import { describeDispositionHarvestLock } from "@lib/harvestLockout";
// A recorded BUY that leaves a line below the trimmable-lot minimum (CIO, 2026-09-17). The
// server decides and refuses with the choices; the modals only render them. See lib/lotMinimum.ts.
import {
  BELOW_MINIMUM_STATUSES,
  isOptionContractId,
  readLotResolutionFlag,
  type LotResolution,
  type LotResolutionFlag,
} from "@lib/lotMinimum";
import {
  AUTO_CAPTURE_MAX_ATTEMPTS,
  LEDGER_CAPTURE_GAP,
  blotterFillKey,
  describeCapture,
  easternSession,
  fillDirection,
  findOpenTicket,
  fmtCalendarDate,
  reconcileBlotterOrder,
  shouldAutoCapture,
  type BlotterLedgerStatus,
} from "@lib/ledgerReconcile";
// Bulk select on the decide lanes (CIO 2026-11-04). One ticket per request, same endpoint.
import {
  SETTLED_KINDS,
  bulkBody,
  classifyResponse,
  countOutcomes,
  reasonGroups,
  splitForBulk,
  summarizeOutcomes,
  type BulkOutcome,
} from "@lib/bulkDecision";
import { useUser } from "../hooks/useUser";
import { LiveBadge } from "@components/cockpit/live";
import { FreshnessChip } from "@components/cockpit/freshness";
import {
  Card,
  SectionCard,
  StatTile,
  chipClass,
  fmtUSD,
  InfoHint,
  MICRO_LABEL,
  type ChipTone,
} from "@components/cockpit";

/**
 * The server's "this fill leaves a line below the minimum" question, asked inside the record
 * dialog. A real execution is never refused outright: the CIO says whether it is an exception
 * or will be topped up, and that answer goes on the audit trail with the trade.
 *
 * Shown only while the quantity typed is the one the server judged; change it and the next
 * submit is judged afresh, so a stale question can never be answered for a different fill.
 */
const LotMinimumQuestion: React.FC<{
  flag: LotResolutionFlag;
  choice: LotResolution | null;
  onChoose: (r: LotResolution) => void;
}> = ({ flag, choice, onChoose }) => (
  <div
    className="mt-3 rounded border px-3 py-2 text-[12px] leading-5"
    style={{
      borderColor: "rgb(var(--c-warn) / 0.55)",
      backgroundColor: "rgb(var(--c-warn) / 0.10)",
      color: "var(--text-primary)",
    }}
    role="group"
    aria-label="Below the contract minimum"
  >
    <strong>
      Below the {flag.minContracts ?? "trimmable"}-contract minimum.
    </strong>{" "}
    {flag.message}
    <div className="mt-2 space-y-1">
      {flag.allowedResolutions.map((r) => (
        <label key={r} className="flex cursor-pointer items-center gap-2">
          <input type="radio" name="lot-minimum-resolution" checked={choice === r} onChange={() => onChoose(r)} />
          <span>{flag.resolutionLabels[r] ?? r}</span>
        </label>
      ))}
    </div>
    <div className="mt-1 text-[11px] opacity-70">Your answer is recorded on the audit trail with the trade.</div>
  </div>
);

/* ==========================================================================
   THE TRADE PAGE IS THE LEDGER.

   Helion executes manually in TWS. This page does not trade — it hands off to
   TWS and records what came back. Two lanes feed one ledger:

     SELL lane   system-initiated. A regime signal, exit hint, DTE breach or
                 home-run trim fires and lands here with its trigger.
     BUY lane    CIO-initiated. Candidates staged from the Screener.

   Both converge on the same act: you trade it in TWS, then record it here with
   the reason. The broker's own execution feed supplies the price — you never
   type one. The reason is the part only you can supply, and it is the part that
   makes the ledger worth reading a year later.
   ========================================================================== */

type ContractIdentity = {
  underlying?: string | null;
  right?: string | null;
  strike?: number | null;
  expiration?: string | null;
  displayName?: string | null;
  description?: string | null;
  heldQty?: number | null;
  dte?: number | null;
  multiplier?: number | null;
  bid?: number | null;
  ask?: number | null;
  mid?: number | null;
  last?: number | null;
  quoteAsOf?: string | null;
};

/** The market read when the ticket was created (dbo.TicketQuotes, 2026-10-23). Measurement only. */
type TicketQuote = {
  status?: string | null;
  detail?: string | null;
  expiry?: string | null;
  strike?: number | null;
  bid?: number | null;
  ask?: number | null;
  last?: number | null;
  mid?: number | null;
  spread_pct?: number | null;
  read_at_utc?: string | null;
  screen_mid?: number | null;
  screen_as_of?: string | null;
};

/** What a fill paid against the mid at ticket. + = worse than mid (paid up / sold down). */
type FillVsQuote = {
  paid_vs_mid_half_spreads?: number | null;
  paid_vs_mid_pct_premium?: number | null;
  quoted_spread_pct?: number | null;
  vs_limit_pct?: number | null;
  basis?: string | null;
};

/** Plain words for a ticket quote that is not a measurement. */
const TICKET_QUOTE_STATUS_TEXT: Record<string, string> = {
  one_sided: "one-sided market",
  prior_close: "market closed (prior close only)",
  no_value: "no quote returned",
  empty: "no quote returned",
  time_budget: "broker read timed out",
  snapshot_error: "broker read failed",
};

/** A board quoted wider than this (spread as % of mid) is flagged as a wide market. */
const WIDE_MARKET_SPREAD = 0.02;

type PendingOrder = {
  orderId: number;
  ticketQuote?: TicketQuote | null;
  /** false = the quote table could not be read: "not measured", never "no quote". */
  ticketQuotesAvailable?: boolean;
  action: string;
  contractId: string;
  symbol?: string | null;
  origin?: string | null;
  scheduledDate?: string | null;
  estimatedQty?: number | null;
  targetNotional?: number | null;
  overrideQty?: number | null;
  overrideNotional?: number | null;
  reason?: string | null;
  status?: string | null;
  limitPrice?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  tags?: string | null;
  contract?: ContractIdentity | null;
  tagsData?: {
    ml?: { score?: number | null; confidence?: string | null; bucket?: string | null; summary?: string | null } | null;
    surface?: { value_label?: string | null; tenor_bucket?: string | null } | null;
  } | null;
};

type LedgerEntry = {
  ledgerId: number;
  entryDate?: string | null;
  side?: string | null;
  action?: string | null;
  contractId?: string | null;
  symbol?: string | null;
  origin?: string | null;
  status?: string | null;
  systemTrigger?: string | null;
  systemReason?: string | null;
  recommendedQty?: number | null;
  filledQty?: number | null;
  avgFillPrice?: number | null;
  grossNotional?: number | null;
  commission?: number | null;
  cioWhy?: string | null;
  actedBy?: string | null;
  actedAtUtc?: string | null;
  unpriced?: boolean;
  followedRecommendation?: boolean | null;
  limitPrice?: number | null;
  ticketQuote?: TicketQuote | null;
  fillVsQuote?: FillVsQuote | null;
  /** The ticket's tags as stored with the trade (D5, 2026-10-27): ML/surface snapshot or the exit engine's context. */
  decisionContext?: Record<string, unknown> | null;
};

type RoundTrip = {
  contractId: string;
  symbol?: string | null;
  status: "OPEN" | "CLOSED" | "NO_POSITION";
  /** Every entry on this contract is a decision NOT to trade. Not a holding. */
  decisionOnly?: boolean;
  netQty: number;
  entryCount: number;
  unpricedCount: number;
  fullyPriced: boolean;
  openingLegMissing: boolean;
  openedOn?: string | null;
  closedOn?: string | null;
  heldDays?: number | null;
  /** Broker-reported, summed across legs. Never netted against anything. */
  commission?: number | null;
  /** What this round-trip IS, in words, straight from the backend. Never a number. */
  positionNote?: string | null;
  /** Names IBKR / NAV as the source of gains and losses. */
  pnlAuthority?: string | null;
  entries: LedgerEntry[];
};

/* NO realizedPnl / realizedPnlBasis / boughtCost / soldProceeds. The ledger stopped
   deriving them on 2026-08-20 -- IBKR and the administrator's NAV are the standing
   truth for gains and losses, and a second figure derived here could only agree by
   luck or disagree with no way to adjudicate. The way it actually failed was worse
   than either: with no opening leg on record, proceeds-minus-zero rendered as a gain
   (+$238,944.40 against true economics near $1,000). Do not re-add these fields. */

type UnmatchedFill = {
  fillId: number;
  execId?: string | null;
  contractId?: string | null;
  symbol?: string | null;
  side?: string | null;
  qty?: number | null;
  price?: number | null;
  commission?: number | null;
  /** Fund sub-account. Two accounts are two trades however alike they look. */
  account?: string | null;
  /** STK / OPT / ETF … decides whether a quantity is shares or contracts. */
  secType?: string | null;
  tradeTimeUtc?: string | null;
  capturedAtUtc?: string | null;
};

/** One TRADE: every execution TWS split it into, gathered back up. */
type FillGroup = {
  key: string;
  symbol: string;
  contractId: string;
  account: string | null;
  secType: string | null;
  /** Normalised from the broker's raw side ('B'/'S', or 'BOT'/'SLD'). */
  direction: "BUY" | "SELL";
  sessionDate: string;
  fills: UnmatchedFill[];
  totalQty: number;
  /** Size-weighted, so a 1-lot at a bad print cannot drag the average. */
  avgPrice: number | null;
  priceLo: number | null;
  priceHi: number | null;
  notional: number | null;
  commission: number | null;
  firstAt: string | null;
  lastAt: string | null;
};

/* ---------------------------------------------------------------- helpers */

// easternSession and fillDirection live in @lib/ledgerReconcile, shared with the
// blotter-to-ledger reconciliation so the queue's grouping and the blotter's tags can
// never date or direct the same trade differently.

/** Group executions into the trades they came from. Exported shape is display-only. */
const groupFills = (fills: UnmatchedFill[]): { groups: FillGroup[]; ungroupable: UnmatchedFill[] } => {
  const byKey = new Map<string, UnmatchedFill[]>();
  const ungroupable: UnmatchedFill[] = [];

  for (const f of fills) {
    const dir = fillDirection(f.side);
    // A non-directional record cannot be recorded as a buy or a sell — the endpoint
    // refuses it with a 400 — so it gets its own bucket rather than a silent home.
    if (!dir) {
      ungroupable.push(f);
      continue;
    }
    const key = [
      f.account ?? "?",
      String(f.contractId ?? ""),
      dir,
      easternSession(f.tradeTimeUtc),
    ].join("|");
    const arr = byKey.get(key);
    if (arr) arr.push(f);
    else byKey.set(key, [f]);
  }

  const groups: FillGroup[] = [];
  byKey.forEach((members, key) => {
    const sorted = [...members].sort((a, b) =>
      String(a.tradeTimeUtc || "").localeCompare(String(b.tradeTimeUtc || "")) ||
      a.fillId - b.fillId
    );
    const totalQty = sorted.reduce((n, f) => n + (f.qty || 0), 0);
    const priced = sorted.filter((f) => typeof f.price === "number");
    const notional = priced.reduce((n, f) => n + (f.qty || 0) * (f.price || 0), 0);
    const prices = priced.map((f) => f.price as number);
    const comms = sorted.filter((f) => typeof f.commission === "number");
    groups.push({
      key,
      symbol: sorted.find((f) => f.symbol)?.symbol || `conid ${sorted[0].contractId}`,
      contractId: String(sorted[0].contractId ?? ""),
      account: sorted[0].account ?? null,
      secType: sorted.find((f) => f.secType)?.secType ?? null,
      direction: fillDirection(sorted[0].side) as "BUY" | "SELL",
      sessionDate: easternSession(sorted[0].tradeTimeUtc),
      fills: sorted,
      totalQty,
      avgPrice: totalQty && priced.length ? notional / totalQty : null,
      priceLo: prices.length ? Math.min(...prices) : null,
      priceHi: prices.length ? Math.max(...prices) : null,
      notional: priced.length ? notional : null,
      commission: comms.length ? comms.reduce((n, f) => n + (f.commission || 0), 0) : null,
      firstAt: sorted[0].tradeTimeUtc ?? null,
      lastAt: sorted[sorted.length - 1].tradeTimeUtc ?? null,
    });
  });

  // Newest trade first, matching the blotter and the old flat list.
  groups.sort((a, b) => String(b.lastAt || "").localeCompare(String(a.lastAt || "")));
  return { groups, ungroupable };
};

/** Shares for a stock/ETF, contracts for an option. Saying "contracts" over a bond ETF
 *  is the options vocabulary the CIO flagged. */
const unitWord = (secType?: string | null, n = 2): string => {
  const t = String(secType || "").toUpperCase();
  if (t === "OPT" || t === "FOP" || t === "WAR") return n === 1 ? "contract" : "contracts";
  if (!t) return n === 1 ? "unit" : "units";
  return n === 1 ? "share" : "shares";
};

const muted: React.CSSProperties = { color: "var(--text-muted)" };
const secondary: React.CSSProperties = { color: "var(--text-secondary)" };

const numOrDash = (v?: number | null) =>
  v == null || Number.isNaN(v) ? "—" : v.toLocaleString();

const px = (v?: number | null) => {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pct = (v?: number | null, digits = 2) =>
  v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(digits)}%`;

/**
 * The market read when this ticket was created (2026-10-23), under the current quote, plus the
 * ticket's limit. "Not measured" (table unreadable) and "no ticket quote" are different claims and
 * say so; a one-sided or closed market is named, never shown as a quote.
 */
function TicketQuoteLine({ order }: { order: PendingOrder }) {
  const q = order.ticketQuote;
  const limit = order.limitPrice != null ? <div className="tabular-nums" style={muted}>limit {px(order.limitPrice)}</div> : null;
  if (order.ticketQuotesAvailable === false) {
    return <div className="mt-1 text-[10px]" style={muted}>ticket quote: not measured{limit}</div>;
  }
  if (order.ticketQuotesAvailable === undefined) return limit ? <div className="mt-1 text-[10px]">{limit}</div> : null;
  if (!q) return <div className="mt-1 text-[10px]" style={muted}>no ticket quote{limit}</div>;
  if (q.status !== "two_sided") {
    return (
      <div className="mt-1 text-[10px]" style={muted} title={q.detail || undefined}>
        at ticket: {TICKET_QUOTE_STATUS_TEXT[q.status || ""] || q.status || "no quote"}
        {limit}
      </div>
    );
  }
  const wide = q.spread_pct != null && q.spread_pct > WIDE_MARKET_SPREAD;
  return (
    <div className="mt-1 text-[10px]" style={secondary} title={q.read_at_utc ? `read ${q.read_at_utc} UTC` : undefined}>
      <div className="tabular-nums">
        at ticket {px(q.bid)} / {px(q.ask)} · {pct((q.spread_pct ?? 0) * 100, 1)} wide
      </div>
      {wide ? (
        <div className="mt-0.5 flex justify-end">
          <span className={chipClass("warning")} title="Quoted wider than 2% of mid when the ticket was created">wide market</span>
        </div>
      ) : null}
      {limit}
    </div>
  );
}

/** What this fill paid against the mid at ticket. + = worse than mid. Nothing is shown when unmeasured. */
function FillVsQuoteLine({ entry }: { entry: LedgerEntry }) {
  const f = entry.fillVsQuote;
  if (!f || f.paid_vs_mid_pct_premium == null) return null;
  const hs = f.paid_vs_mid_half_spreads;
  const bad = hs != null && hs > 1;
  const sign = (v: number) => (v > 0 ? "+" : "");
  return (
    <div
      className="text-[10px]"
      style={bad ? { color: "rgb(var(--c-warn))" } : muted}
      title={`Against the mid read when the ticket was created; quoted spread then ${pct(f.quoted_spread_pct, 2)}. Positive = paid above mid on a buy / sold below mid on a sell. Intraday moves between ticket and fill are included.`}
    >
      vs mid at ticket {sign(f.paid_vs_mid_pct_premium)}{pct(f.paid_vs_mid_pct_premium, 2)}
      {hs != null ? ` (${sign(hs)}${hs.toFixed(2)} hs)` : ""}
    </div>
  );
}

/** What the system recorded when the ticket was made (D5). Top-level facts only; nested blocks summarised. */
function DecisionContextDetails({ ctx }: { ctx?: Record<string, unknown> | null }) {
  if (!ctx || typeof ctx !== "object") return null;
  const entries = Object.entries(ctx).filter(([k]) => k !== "_truncated");
  if (!entries.length) return null;
  const show = (v: unknown): string => {
    if (v == null) return "—";
    if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(3);
    if (typeof v === "string" || typeof v === "boolean") return String(v);
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      return Object.entries(o).filter(([, x]) => x == null || typeof x !== "object").slice(0, 3)
        .map(([k, x]) => `${k}: ${x == null ? "—" : String(x)}`).join(" · ") || "…";
    }
    return String(v);
  };
  return (
    <details className="mt-1 text-[10px]" style={muted} data-testid="decision-context">
      <summary className="cursor-pointer">recorded at decision</summary>
      <div className="mt-0.5 space-y-0.5">
        {entries.slice(0, 10).map(([k, v]) => (
          <div key={k}><span className="font-semibold">{k}</span>: {show(v)}</div>
        ))}
        {(ctx as any)._truncated ? <div>(some fields were too long to store)</div> : null}
      </div>
    </details>
  );
}

type BoardRow = {
  board: string;
  fills: number;
  paidVsMidPctPremiumMedian?: number | null;
  paidVsMidHalfSpreadsMedian?: number | null;
  quotedSpreadPctMedian?: number | null;
};

/** Execution cost by expiry board, from fills measured against the quote at ticket. */
function FillVsQuoteByBoard({ ledger }: { ledger: any }) {
  if (!ledger || ledger.ledgerAvailable === false || ledger.ticketQuotesAvailable === undefined) return null;
  if (ledger.ticketQuotesAvailable === false) {
    return (
      <div className="rounded-lg border px-3 py-2 text-[12px]" style={{ borderColor: "var(--card-border)", ...muted }} data-testid="fill-vs-quote-board">
        Execution cost by expiry: not measured yet (the ticket-quote table is not available).
      </div>
    );
  }
  const rows: BoardRow[] = ledger.fillVsQuoteByBoard?.boards || [];
  return (
    <div className="rounded-lg border px-3 py-2 text-[12px]" style={{ borderColor: "var(--card-border)" }} data-testid="fill-vs-quote-board">
      <div className="font-semibold">Execution cost by expiry board</div>
      <div className="text-[11px]" style={muted}>
        Each fill against the mid read when its ticket was created. Medians; positive = paid above mid.
      </div>
      {rows.length === 0 ? (
        <div className="mt-1" style={muted}>No fills measured against a ticket quote yet.</div>
      ) : (
        <table className="mt-1 w-full text-[12px]">
          <thead>
            <tr style={muted}>
              <th scope="col" className="pr-3 text-left font-semibold">Board</th>
              <th scope="col" className="pr-3 text-right font-semibold">Fills</th>
              <th scope="col" className="pr-3 text-right font-semibold">Paid vs mid</th>
              <th scope="col" className="pr-3 text-right font-semibold">Half-spreads</th>
              <th scope="col" className="text-right font-semibold">Quoted spread</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.board} className="border-t" style={{ borderColor: "var(--card-border)" }}>
                <td className="pr-3 tabular-nums">{r.board}</td>
                <td className="pr-3 text-right tabular-nums">{r.fills}</td>
                <td className="pr-3 text-right tabular-nums">{pct(r.paidVsMidPctPremiumMedian, 2)}</td>
                <td className="pr-3 text-right tabular-nums">{r.paidVsMidHalfSpreadsMedian == null ? "—" : r.paidVsMidHalfSpreadsMedian.toFixed(2)}</td>
                <td className="text-right tabular-nums">{pct(r.quotedSpreadPctMedian, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Effective size mirrors the backend: a non-zero override wins, else the estimate. */
const effectiveQty = (o: PendingOrder) =>
  o.overrideQty != null && o.overrideQty !== 0 ? o.overrideQty : o.estimatedQty;
const effectiveNotional = (o: PendingOrder) =>
  o.overrideNotional != null && o.overrideNotional !== 0 ? o.overrideNotional : o.targetNotional;
const hasOverride = (o: PendingOrder) =>
  (o.overrideQty != null && o.overrideQty !== 0 && o.overrideQty !== o.estimatedQty) ||
  (o.overrideNotional != null && o.overrideNotional !== 0 && o.overrideNotional !== o.targetNotional);

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** "2031-12-19" -> "19DEC2031". Returns null if it is not a parseable date. */
const fmtExpiry = (iso?: string | null): string | null => {
  const s = String(iso || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${m[3]}${mon}${m[1]}` : null;
};

/** A ticker (ETF / fixed-income / alts), as opposed to a numeric IBKR conid. */
const isTickerId = (contractId?: string | null): boolean => {
  const s = String(contractId || "").trim();
  return s.length > 0 && s.length <= 12 && !/^\d+$/.test(s);
};

/**
 * THE contract label. One rule for the whole desk.
 *
 * OPTIONS get the full standardised name — underlying, expiry, right, strike:
 *
 *     SPX 19DEC2031 C8000
 *
 * On an all-SPX book the underlying alone identifies nothing, and "which contract"
 * is the entire question when you are about to key an order into TWS. Strike and
 * expiry are not decoration here; they are the trade.
 *
 * ETFs / FIXED INCOME / ALTS get the plain ticker, because for those the ticker IS
 * the whole identity — there is no strike or expiry to state. The alternatives and
 * FI engines stage a TICKER in ContractId rather than a numeric conid, which is how
 * we tell the two apart without needing a sleeve flag.
 *
 * Structured fields are preferred over any stored label, so a ticket staged before
 * the label fix still renders correctly once its contract is resolvable. Nothing
 * ever renders blank: a missing label on a trade surface reads as "nothing here"
 * rather than "we could not resolve this".
 */
const contractLabel = (c?: ContractIdentity | null, symbol?: string | null, contractId?: string | null): string => {
  // 1. Full option name, built from structured fields — the most reliable source.
  if (c?.underlying && c?.expiration && c?.strike != null) {
    const exp = fmtExpiry(c.expiration) || String(c.expiration).slice(0, 10);
    const right = String(c.right || "").toUpperCase().slice(0, 1);
    const strike = Number(c.strike).toLocaleString(undefined, { maximumFractionDigits: 2 }).replace(/,/g, "");
    return `${c.underlying} ${exp} ${right}${strike}`.replace(/\s+/g, " ").trim();
  }
  // 2. A name the broker/view already composed.
  if (c?.displayName) return c.displayName;
  if (c?.description) return c.description;
  // 3. A label staged with the ticket (the Screener writes the full option name here).
  if (symbol && symbol.trim()) return symbol.trim();
  // 4. A ticker instrument — the ticker is the identity, not a degraded label.
  if (isTickerId(contractId)) return String(contractId).trim().toUpperCase();
  return contractId ? `conid ${contractId}` : "—";
};

const originTone = (origin?: string | null): ChipTone => {
  const o = (origin || "SYSTEM").toUpperCase();
  if (o === "CIO") return "info";
  if (o === "SCREENER") return "positive";
  return "neutral";
};

const actionTone = (action?: string | null): ChipTone => {
  const a = (action || "").toUpperCase();
  if (a === "BUY") return "positive";
  if (a === "TRIM") return "warning";
  if (a === "ROLL") return "info";
  return "danger"; // SELL
};

const msOf = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/* The live blotter's feed-down detector — unchanged. It is deliberately tuned NOT
   to cry wolf on transient IBKR errors: a partial/flaky read while orders are
   still flowing is not a dead feed. (2026-07-06 fix, preserved.) */
const isLiveFeedDown = (lo: any): boolean => {
  if (!lo) return false;
  const errs: string[] = Array.isArray(lo.errors) ? lo.errors : [];
  const hasData = ((lo.counts?.working_orders ?? 0) > 0) || ((lo.counts?.fills_today ?? 0) > 0);
  if (hasData) return false;
  const hardErr = errs.includes("import") || errs.includes("subaccounts") || errs.includes("orders");
  return hardErr || (lo.counts?.subaccounts ?? 0) === 0;
};

const fmtTradeTimeGmt = (tt?: string | null): string => {
  if (!tt || tt.length < 15) return tt ?? "—";
  const iso = `${tt.slice(0, 4)}-${tt.slice(4, 6)}-${tt.slice(6, 8)}T${tt.slice(9)}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? tt : d.toLocaleTimeString();
};

const orderStatusChipTone = (s?: string | null): ChipTone => {
  const v = (s || "").toLowerCase();
  if (v.includes("fill")) return "positive";
  if (v.includes("cancel")) return "neutral";
  if (v.includes("reject") || v.includes("error")) return "danger";
  return "warning";
};

/* ============================= record a discretionary trade ================
 * Claims broker executions that no ledger entry accounts for. Every other route
 * into the ledger starts from a ticket; a trade made straight in TWS has none, so
 * without this its executions sit in the break queue forever with no reason
 * attached and the round-trip netting never sees the leg.
 *
 * Evidence first: contract, side, quantity and price all come from the broker's
 * own fills. The only thing asked for is the reason. There is deliberately no way
 * here to record a trade with no execution behind it.
 *
 * Several fills are one decision. TWS routinely splits an order, and charging a
 * separate reason per partial fill would be a tax on telling the truth.
 */
const ClaimFillsModal = ({
  fills, group, truncated, queueUnavailable, busy, actor, lotFlag, onConfirm, onCancel,
}: {
  fills: UnmatchedFill[];
  /** The trade that owns EVERY selected fill. Null means the selection spans trades. */
  group?: FillGroup | null;
  /** The queue is showing only part of the backlog, so no group can be called whole. */
  truncated?: boolean;
  queueUnavailable?: boolean;
  busy: boolean;
  actor: string | null;
  /** Set when the server asked the lot-minimum question for THIS selection. */
  lotFlag?: LotResolutionFlag | null;
  onConfirm: (why: string, action: string, lotMinimumResolution?: LotResolution) => void;
  onCancel: () => void;
}) => {
  const [why, setWhy] = useState("");
  const [lotChoice, setLotChoice] = useState<LotResolution | null>(null);
  // A new question (re-asked, or for a different quantity or selection) starts unanswered.
  useEffect(() => { setLotChoice(null); }, [lotFlag]);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const isBuy = String(fills[0]?.side || "").toUpperCase().startsWith("B");
  const [action, setAction] = useState<string>(isBuy ? "BUY" : "SELL");
  // Shares for an ETF, contracts for an option. "Contracts worked" over a bond ETF was
  // the options vocabulary the CIO flagged.
  const secType = fills.find((f) => f.secType)?.secType ?? null;
  const isOption = ["OPT", "FOP", "WAR"].includes(String(secType || "").toUpperCase());
  const totalQty = fills.reduce((n, f) => n + (f.qty || 0), 0);
  // Quantity-weighted, so a 1-lot at a bad print cannot drag the average.
  const notional = fills.reduce((n, f) => n + (f.qty || 0) * (f.price || 0), 0);
  const avgPx = totalQty ? notional / totalQty : null;
  const contracts = Array.from(new Set(fills.map((f) => String(f.contractId))));
  const sides = Array.from(new Set(fills.map((f) => String(f.side || "").toUpperCase().charAt(0))));
  const mixed = contracts.length > 1 || sides.length > 1;
  // The question applies only to the quantity it was asked about.
  const lotQuestion = lotFlag && lotFlag.recordedQty === totalQty ? lotFlag : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(2, 8, 23, 0.62)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Record a trade you made"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl p-5"
        style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--card-border)", boxShadow: "var(--shadow-soft)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={MICRO_LABEL} style={muted}>Record a trade you made</div>
        <h3 className="mt-1 text-[17px] font-semibold">
          {fills[0]?.symbol || `conid ${fills[0]?.contractId}`}
        </h3>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          <div><span style={muted}>Executions</span><div className="font-semibold tabular-nums">{fills.length}</div></div>
          <div>
            <span style={muted}>Total quantity</span>
            <div className="font-semibold tabular-nums">
              {totalQty.toLocaleString()}
              <span className="ml-1 text-[11px] font-normal" style={muted}>{unitWord(secType, totalQty)}</span>
            </div>
          </div>
          <div><span style={muted}>Average price</span><div className="font-semibold tabular-nums">{px(avgPx)}</div></div>
          <div><span style={muted}>Direction</span><div className="font-semibold">{isBuy ? "BUY" : "SELL"}</div></div>
        </div>

        {mixed ? (
          <div className="mt-3 text-[12px] leading-5" style={{ color: "rgb(var(--c-down))" }}>
            These executions are not all the same contract and direction. Select one
            contract and one direction — recording them together would net the wrong way.
          </div>
        ) : !group ? (
          /* No single trade owns this selection. The endpoint validates only contract
             and direction, so it would merge two sessions into one entry rather than
             refuse -- this is the only place that can stop it. */
          <div className="mt-3 text-[12px] leading-5" style={{ color: "rgb(var(--c-down))" }}>
            These executions come from <strong>more than one trade</strong>. Recording them
            together would write a single ledger entry dated to the earlier session, with a
            quantity matching neither. Select one trade at a time.
          </div>
        ) : group && fills.length < group.fills.length ? (
          /* Consequence first: what is left behind, and where it goes. */
          <div
            className="mt-3 rounded-md px-3 py-2 text-[12px] leading-5"
            style={{ backgroundColor: "rgb(var(--c-warn) / 0.10)", border: "1px solid rgb(var(--c-warn) / 0.32)" }}
          >
            Recording <strong>{fills.length} of {group.fills.length}</strong> executions
            {" "}({totalQty.toLocaleString()} of {group.totalQty.toLocaleString()}{" "}
            {unitWord(group.secType, group.totalQty)}). The{" "}
            {group.fills.length - fills.length === 1 ? "one you left out stays" : "ones you left out stay"}{" "}
            in the queue as a separate break.
          </div>
        ) : truncated ? (
          /* The queue is capped, so this group may be a fragment of a larger trade whose
             remaining executions were cut off. Silence beats a completeness claim we
             cannot support. */
          <div className="mt-3 text-[12px] leading-5" style={{ color: "rgb(var(--c-warn))" }}>
            The queue is showing only part of the backlog, so this may not be the whole
            trade. Check the executions below before recording.
          </div>
        ) : (
          <div className="mt-3 text-[12px] leading-5" style={{ color: "rgb(var(--c-up))" }}>
            &#10003; All {group.fills.length} {group.fills.length === 1 ? "execution" : "executions"} on
            this {group.symbol} {group.direction === "BUY" ? "purchase" : "sale"} are included.
          </div>
        )}

        {!isBuy ? (
          <div className="mt-3">
            <span className={MICRO_LABEL} style={muted}>How to record it</span>
            <div className="mt-1 flex gap-2">
              {(isOption ? ["SELL", "TRIM", "ROLL"] : ["SELL", "TRIM"]).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAction(a)}
                  className="rounded-md px-3 py-1.5 text-[12px] font-semibold border"
                  style={{
                    borderColor: action === a ? "rgb(var(--c-accent))" : "var(--card-border)",
                    color: action === a ? "rgb(var(--c-accent))" : undefined,
                    opacity: action === a ? 1 : 0.7,
                  }}
                >
                  {a === "SELL"
                    ? isOption ? "Sold out" : "Sold out of it"
                    : a === "TRIM"
                      ? isOption ? "Trimmed" : "Reduced the position"
                      : "Rolled"}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <p className="mt-3 text-[12px] leading-5" style={secondary}>
          The quantity and price come from the <strong>broker&rsquo;s own execution feed</strong> —
          you are not entering a trade, you are recording why one happened. Nothing is sent
          to the broker.
        </p>

        {lotQuestion ? <LotMinimumQuestion flag={lotQuestion} choice={lotChoice} onChoose={setLotChoice} /> : null}

        <label className="mt-4 block">
          <span className={MICRO_LABEL} style={muted}>
            Why did you make this trade? <span style={{ color: "rgb(var(--c-warn))" }}>*</span>
            <InfoHint text="Required. This is the whole reason the entry is worth keeping — the broker already told us what happened, only you can say why." />
          </span>
          <textarea
            ref={ref}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            rows={3}
            placeholder={
              isOption
                ? "e.g. took the Dec-28 6700 off into the spike; funding the Jan-29 ladder"
                : "e.g. cut duration ahead of the refunding; rotated the proceeds into floating rate"
            }
            className="mt-1 w-full rounded-md px-3 py-2 text-[13px]"
            style={{ backgroundColor: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)" }}
          />
        </label>

        <div className="mt-2 text-[11px]" style={muted}>
          Recorded as {actor || <span style={{ color: "rgb(var(--c-warn))" }}>not signed in</span>}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <HelionButton size="sm" onClick={onCancel} disabled={busy}>Cancel</HelionButton>
          <HelionButton
            size="sm"
            onClick={() => onConfirm(why, action, lotQuestion ? lotChoice ?? undefined : undefined)}
            disabled={busy || !actor || !why.trim() || mixed || !group || queueUnavailable || (!!lotQuestion && !lotChoice)}
            className="font-semibold"
            style={{ borderColor: "rgb(var(--c-accent))" }}
          >
            {busy ? "Recording…" : "Record in the ledger"}
          </HelionButton>
        </div>
        {actor && !why.trim() ? (
          <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
            A reason is required — the ledger must say why.
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ====================================================== the decision modal */

/* Four outcomes, not two. "Cancelled" used to flatten three different decisions:
   killing a ticket, refusing a recommendation, and postponing one. After the fact
   the ledger could not tell them apart. dbo.TradeLedger.Status already permitted
   all four - only EXECUTED and CANCELLED were ever reachable from here.

   DEFERRED deliberately leaves the ticket LIVE: the recommendation still stands,
   you have just not acted yet, so it stays in the queue and earns a ledger row
   each time you look at it. */
type Decision = "executed" | "cancelled" | "declined" | "deferred";

/** What the CIO says they did. All optional; blank means "not recorded", not zero. */
type DecisionActuals = {
  actedQty?: string;
  cioFilledQty?: string;
  cioAvgPrice?: string;
  /** Deferrals only: when to raise this again. Blank = no date, held until you act. */
  revisitOn?: string;
  /** The answer to the server's lot-minimum question, when it asked one. */
  lotMinimumResolution?: LotResolution;
};

const DECISION_COPY: Record<Decision, { title: string; confirm: string; blurb: string; whyLabel: string; whyPlaceholder: string }> = {
  executed: {
    title: "Record trade in the ledger",
    confirm: "Record trade",
    blurb: "Records what you actually did in TWS. The ticket is retired.",
    whyLabel: "Why",
    whyPlaceholder: "e.g. grind defense fired — cut exposure, kept the Dec-28s",
  },
  declined: {
    title: "Decline recommendation",
    confirm: "Record decline",
    blurb:
      "Records that you were shown this and chose not to trade it. That is a ledger entry in its own right, and the ticket is retired.",
    whyLabel: "Why not",
    whyPlaceholder: "e.g. signal fired on a stale mark; contract is illiquid this week",
  },
  deferred: {
    title: "Defer for now",
    confirm: "Record deferral",
    blurb:
      "Records that you looked and chose to wait. The recommendation STAYS in the queue and can be actioned later.",
    whyLabel: "Why not yet",
    whyPlaceholder: "e.g. waiting for the Sep roll; revisit after CPI",
  },
  cancelled: {
    title: "Cancel the ticket",
    confirm: "Cancel ticket",
    blurb: "Kills the recommendation outright. Use this when the ticket itself is wrong or stale.",
    whyLabel: "Why cancel this ticket",
    whyPlaceholder: "e.g. ticket was raised on a bad mark; superseded by today's run",
  },
};

/* The exit engine writes short snake_case triggers ('home_run_trim', 'dte_breach') where
   the rebalance engine writes prose, and the lane printed the raw token next to sentences —
   `home_run_trim` reads as a leaked identifier, and it is the one trigger that now carries a
   consequence (a 60-day redeployment lockout) the CIO should see before clicking. Unknown
   tokens are left exactly as they arrive: a new engine reason should look unfamiliar rather
   than be dressed up as a known one. */
const TRIGGER_LABELS: Record<string, string> = {
  home_run_trim: "Home-run trim — 2× harvest (starts the 60-day redeployment lockout)",
  partial_trim: "Partial trim",
  dte_breach: "DTE breach",
  expired: "Expired",
};

const reasonLabel = (reason?: string | null): string | null => {
  const text = String(reason ?? "").trim();
  if (!text) return null;
  return TRIGGER_LABELS[text] ?? text;
};

/** One sentence for the success banner when a BUY was recorded below the minimum; "" otherwise. */
function describeRecordedLot(lot: any, askedFlag?: LotResolutionFlag | null): string {
  const status = String(lot?.status || "");
  if (!BELOW_MINIMUM_STATUSES.includes(status)) return "";
  const min = Number.isFinite(Number(lot?.minContracts)) ? Number(lot.minContracts) : null;
  const floor = min != null ? `the ${min}-contract minimum` : "the contract minimum";
  if (status === "exception_on_ticket") return ` Below ${floor}, under the exception given when it was queued.`;
  const key = lot?.resolution as LotResolution | undefined;
  const label = key ? askedFlag?.resolutionLabels[key] ?? key : "no answer recorded";
  return ` Below ${floor} — recorded as: ${label}.`;
}

const DecisionModal: React.FC<{
  order: PendingOrder;
  decision: Decision;
  busy: boolean;
  actor: string | null;
  /** Set when the server asked the lot-minimum question for THIS ticket. */
  lotFlag?: LotResolutionFlag | null;
  onConfirm: (why: string, actuals: DecisionActuals) => void;
  onCancel: () => void;
}> = ({ order, decision, busy, actor, lotFlag, onConfirm, onCancel }) => {
  const [why, setWhy] = useState("");
  const [lotChoice, setLotChoice] = useState<LotResolution | null>(null);
  // A new question (re-asked, or for a different quantity or selection) starts unanswered.
  useEffect(() => { setLotChoice(null); }, [lotFlag]);
  // What you actually worked, and what TWS shows - both optional, both stored
  // SEPARATELY from the broker figures so neither overwrites the other.
  const [actedQty, setActedQty] = useState<string>("");
  const [cioFilledQty, setCioFilledQty] = useState<string>("");
  const [cioAvgPrice, setCioAvgPrice] = useState<string>("");
  // Deferrals only. Defaults to a week out: a deferral with no date relies on you
  // remembering, and the whole point of deferring is that you are busy now.
  const [revisitOn, setRevisitOn] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const label = contractLabel(order.contract, order.symbol, order.contractId);
  const recording = decision === "executed";
  /* A 2x harvest. The exit engine tags these `home_run_trim`, and recording one starts
     the 60-calendar-day redeployment lockout on the harvested capital -- which is
     computed from the two figures below, so here they stop being optional. Before
     2026-09-02 the lockout could only be started by a SECOND record on the Signals
     harvest panel, and recording the trade here left the clock unstarted with nothing
     saying so. */
  const isHarvest = recording && String(order.reason || "").trim() === "home_run_trim";
  const copy = DECISION_COPY[decision];
  const recommended = effectiveQty(order);
  const actedNum = actedQty.trim() === "" ? null : Number(actedQty);
  const qtyInvalid = actedNum !== null && (!Number.isFinite(actedNum) || actedNum < 0);
  const isPartial =
    actedNum !== null &&
    Number.isFinite(actedNum) &&
    typeof recommended === "number" &&
    actedNum > 0 &&
    actedNum < recommended;
  // The backend reads cioFilledQty first and falls back to actedQty, so accept either
  // here rather than demanding the CIO type the same number into two boxes.
  const filledNum = cioFilledQty.trim() === "" ? null : Number(cioFilledQty);
  const priceNum = cioAvgPrice.trim() === "" ? null : Number(cioAvgPrice);
  const harvestQty = filledNum !== null && Number.isFinite(filledNum) && filledNum > 0
    ? filledNum
    : actedNum !== null && Number.isFinite(actedNum) && actedNum > 0
    ? actedNum
    : null;
  const harvestPrice = priceNum !== null && Number.isFinite(priceNum) && priceNum > 0 ? priceNum : null;
  const harvestReady = !isHarvest || (harvestQty !== null && harvestPrice !== null);
  // An option BUY must say how many filled: the fill decides whether the line meets the
  // trimmable-lot minimum, and a blank one skipped that check exactly in the partial-fill case
  // (review finding 2026-09-17). The server refuses it too (lot_minimum_fill_required).
  const isOptionBuy = recording && String(order.action || "").toUpperCase() === "BUY" && isOptionContractId(order.contractId);
  const fillReady = !isOptionBuy || harvestQty !== null;
  // The server judges the fill if typed, else what was worked -- the same precedence here, so
  // the question shows only for the quantity it was asked about.
  const recordedForLot = filledNum !== null ? filledNum : actedNum;
  const lotQuestion = recording && lotFlag && lotFlag.recordedQty === recordedForLot ? lotFlag : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(2, 8, 23, 0.62)" }}
      role="dialog"
      aria-modal="true"
      aria-label={recording ? "Record trade" : "Decline recommendation"}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl p-5"
        style={{
          backgroundColor: "var(--card-bg)",
          border: "1px solid var(--card-border)",
          boxShadow: "var(--shadow-soft)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={MICRO_LABEL} style={muted}>{copy.title}</div>
        <h3 className="mt-1 text-[17px] font-semibold">{label}</h3>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          <div><span style={muted}>Action</span><div className="font-semibold">{order.action}</div></div>
          <div><span style={muted}>Recommended</span><div className="font-semibold tabular-nums">{numOrDash(effectiveQty(order))}</div></div>
          <div><span style={muted}>Notional</span><div className="font-semibold tabular-nums">{fmtUSD(effectiveNotional(order))}</div></div>
          <div><span style={muted}>Ticket</span><div className="font-semibold tabular-nums">#{order.orderId}</div></div>
          {/* The contract you are about to key into TWS, spelled out. A confirmation
              that says only "SPX" on an all-SPX book confirms nothing. */}
          <div className="col-span-2">
            <span style={muted}>Contract</span>
            <div className="font-semibold tabular-nums">
              {[
                order.contract?.strike != null ? `strike ${order.contract.strike.toLocaleString()}` : null,
                order.contract?.expiration ? `exp ${String(order.contract.expiration).slice(0, 10)}` : null,
                order.contract?.right ? String(order.contract.right).toUpperCase() : null,
              ].filter(Boolean).join(" · ") || label}
            </div>
            <div className="text-[11px]" style={muted}>conid {order.contractId || "—"}</div>
          </div>
        </div>

        <p className="mt-3 text-[12px] leading-5" style={secondary}>
          {copy.blurb} Nothing is sent to the broker &mdash; every order is placed by hand in TWS.
        </p>

        {recording ? (
          <div className="mt-3 rounded-md p-3" style={{ backgroundColor: "var(--bg-secondary)" }}>
            <div className={MICRO_LABEL} style={muted}>What you actually did</div>

            {isHarvest ? (
              <div
                className="mt-2 rounded border px-3 py-2 text-[12px] leading-5"
                style={{
                  borderColor: "rgb(var(--c-accent) / 0.45)",
                  backgroundColor: "rgb(var(--c-accent) / 0.08)",
                  color: "var(--text-primary)",
                }}
              >
                <strong>Home-run trim.</strong> If this TRIMS the line, recording it starts the
                60-calendar-day redeployment lockout on the harvested capital, struck on the
                figures you enter below &mdash; so contracts filled and average price are
                required, not optional. If the fill took the WHOLE line it is a full exit, not a
                harvest: no lockout starts, because there is no runner to redeploy around. The
                decision is made from the FILL against the held book, and the confirmation below
                will say which happened.
              </div>
            ) : null}

            <label className="mt-2 block">
              <span className="text-[12px]" style={secondary}>
                Contracts worked
                <InfoHint text="What YOU decided to trade, which may be fewer than the recommendation - a home-run trim is exactly this. Leave blank if you would rather not record it; it is stored as 'not recorded', never as zero." />
              </span>
              <input
                inputMode="numeric"
                value={actedQty}
                onChange={(e) => setActedQty(e.target.value)}
                placeholder={typeof recommended === "number" ? String(recommended) + " recommended" : "e.g. 2"}
                className="mt-1 w-full rounded-md px-3 py-2 text-[13px] tabular-nums"
                style={{
                  backgroundColor: "var(--input-bg)",
                  border: qtyInvalid ? "1px solid rgb(var(--c-down))" : "1px solid var(--input-border)",
                  color: "var(--text-primary)",
                }}
              />
              {isPartial ? (
                <span className="mt-1 block text-[11px]" style={{ color: "rgb(var(--c-accent))" }}>
                  Partial &mdash; {actedNum} of {recommended}. The rest stays in the position.
                </span>
              ) : null}
              {qtyInvalid ? (
                <span className="mt-1 block text-[11px]" style={{ color: "rgb(var(--c-down))" }}>
                  Must be a number of contracts, and not negative.
                </span>
              ) : null}
            </label>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[12px]" style={secondary}>
                  Filled per TWS{isHarvest || isOptionBuy ? <span style={{ color: "rgb(var(--c-warn))" }}> *</span> : null}
                </span>
                <input
                  inputMode="numeric"
                  value={cioFilledQty}
                  onChange={(e) => setCioFilledQty(e.target.value)}
                  placeholder={isHarvest || isOptionBuy ? (harvestQty !== null ? "" : "required") : "optional"}
                  className="mt-1 w-full rounded-md px-3 py-2 text-[13px] tabular-nums"
                  style={{
                    backgroundColor: "var(--input-bg)",
                    border: (isHarvest || isOptionBuy) && harvestQty === null ? "1px solid rgb(var(--c-warn))" : "1px solid var(--input-border)",
                    color: "var(--text-primary)",
                  }}
                />
                {(isHarvest || isOptionBuy) && harvestQty !== null && filledNum === null ? (
                  <span className="mt-1 block text-[11px]" style={muted}>
                    Using the {harvestQty} from Contracts worked.
                  </span>
                ) : null}
              </label>
              <label className="block">
                <span className="text-[12px]" style={secondary}>
                  Avg price per TWS{isHarvest ? <span style={{ color: "rgb(var(--c-warn))" }}> *</span> : null}
                </span>
                <input
                  inputMode="decimal"
                  value={cioAvgPrice}
                  onChange={(e) => setCioAvgPrice(e.target.value)}
                  placeholder={isHarvest ? "required" : "optional"}
                  className="mt-1 w-full rounded-md px-3 py-2 text-[13px] tabular-nums"
                  style={{
                    backgroundColor: "var(--input-bg)",
                    border: isHarvest && harvestPrice === null ? "1px solid rgb(var(--c-warn))" : "1px solid var(--input-border)",
                    color: "var(--text-primary)",
                  }}
                />
              </label>
            </div>

            <p className="mt-2 text-[11px] leading-5" style={muted}>
              These two are <strong>your</strong> record, kept separate from the broker&rsquo;s. IBKR&rsquo;s
              own fill and price still attach automatically from the execution feed and never
              overwrite what you type; where the two disagree the ledger shows a break rather than
              picking a winner. Worth filling in when the feed has not caught up &mdash; it only
              reaches back 7 days, so anything older can never be recovered automatically.
            </p>

            {lotQuestion ? <LotMinimumQuestion flag={lotQuestion} choice={lotChoice} onChoose={setLotChoice} /> : null}
          </div>
        ) : null}

        {decision === "deferred" ? (
          <label className="mt-3 block">
            <span className={MICRO_LABEL} style={muted}>
              Revisit on
              <InfoHint text="Until this date the Risk watchlist stops escalating this position to 'Act now' and shows the deferral instead. The ticket stays open in this queue the whole time. Clear the date to hold it until you act." />
            </span>
            <input
              type="date"
              value={revisitOn}
              onChange={(e) => setRevisitOn(e.target.value)}
              className="mt-1 w-full rounded-md px-3 py-2 text-[13px] tabular-nums"
              style={{ backgroundColor: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)" }}
            />
            <span className="mt-1 block text-[11px]" style={muted}>
              {revisitOn
                ? `Quiet until ${revisitOn}, then it returns to Act now on its own.`
                : "No date — it stays quiet until you disposition it. The ticket remains in this queue."}
            </span>
          </label>
        ) : null}

        <label className="mt-4 block">
          <span className={MICRO_LABEL} style={muted}>
            {copy.whyLabel} <span style={{ color: "rgb(var(--c-warn))" }}>*</span>
            <InfoHint text="Required. The reason is the half of the ledger only you can supply — it is what makes the record worth reading later. An entry without it records that something happened and loses the only part worth reading." />
          </span>
          <textarea
            ref={ref}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            rows={3}
            placeholder={copy.whyPlaceholder}
            className="mt-1 w-full rounded-md px-3 py-2 text-[13px]"
            style={{
              backgroundColor: "var(--input-bg)",
              border: "1px solid var(--input-border)",
              color: "var(--text-primary)",
            }}
          />
        </label>

        <div className="mt-2 text-[11px]" style={muted}>
          Recorded as {actor || <span style={{ color: "rgb(var(--c-warn))" }}>not signed in</span>}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <HelionButton size="sm" onClick={onCancel} disabled={busy}>Cancel</HelionButton>
          <HelionButton
            size="sm"
            onClick={() =>
              onConfirm(why, {
                actedQty: actedQty.trim() || undefined,
                cioFilledQty: cioFilledQty.trim() || undefined,
                cioAvgPrice: cioAvgPrice.trim() || undefined,
                revisitOn: decision === "deferred" ? revisitOn.trim() || undefined : undefined,
                lotMinimumResolution: lotQuestion ? lotChoice ?? undefined : undefined,
              })
            }
            disabled={busy || !actor || qtyInvalid || !why.trim() || !harvestReady || !fillReady || (!!lotQuestion && !lotChoice)}
            className="font-semibold"
            style={{ borderColor: "rgb(var(--c-accent))" }}
          >
            {busy ? "Recording…" : copy.confirm}
          </HelionButton>
        </div>
        {!actor ? (
          <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
            Sign-in required — the ledger must name who decided.
          </div>
        ) : null}
        {actor && !why.trim() ? (
          <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
            A reason is required — the ledger must say why.
          </div>
        ) : null}
        {actor && why.trim() && harvestReady && !fillReady ? (
          <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
            Contracts filled is required for an option buy — the fill decides whether the line meets the
            contract minimum for a 2x trim.
          </div>
        ) : null}
        {actor && why.trim() && !harvestReady ? (
          <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
            Contracts filled and average price are required — they strike the 60-calendar-day redeployment
            lockout when this is a trim. A fill that closes the whole line is a full exit and starts no
            lockout.
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ============================================================= bulk dialog */

/* Bulk decisions (CIO 2026-11-04). The same four outcomes as the single dialog, for a group
   of tickets that share one reason. It records nothing until confirmed, lists every ticket it
   will touch (and, for "I traded these", every ticket it leaves out and why), then records
   ticket by ticket and reports each one. See lib/bulkDecision.ts for what stays single. */
const BULK_OUTCOME_TONE: Record<BulkOutcome["kind"], ChipTone> = {
  recorded: "positive",
  already: "neutral",
  needs_single: "warning",
  recorded_with_problem: "danger",
  failed: "danger",
  not_attempted: "warning",
};
const BULK_OUTCOME_LABEL: Record<BulkOutcome["kind"], string> = {
  recorded: "recorded",
  already: "already decided",
  needs_single: "do on its own",
  recorded_with_problem: "recorded — problem",
  failed: "failed",
  not_attempted: "not sent",
};

// Props typed on the parameter, not through React.FC: without @types/react, React.FC is
// `any` and would leave these unchecked (see the note on RoundTripCard).
type BulkDialogProps = {
  decision: Decision;
  tickets: PendingOrder[];
  actor: string | null;
  run: { total: number; outcomes: BulkOutcome[]; finished: boolean } | null;
  onConfirm: (why: string, revisitOn?: string) => void;
  onStop: () => void;
  onClose: () => void;
};

const BulkDecisionModal = ({ decision, tickets, actor, run, onConfirm, onStop, onClose }: BulkDialogProps) => {
  const [why, setWhy] = useState("");
  const [stopping, setStopping] = useState(false);
  const [revisitOn, setRevisitOn] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const running = !!run && !run.finished;

  useEffect(() => {
    ref.current?.focus();
  }, []);
  useEffect(() => {
    // Escape closes only before the run starts or after it ends: closing mid-run would hide
    // a recording that is still going on.
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !running) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, running]);

  const copy = DECISION_COPY[decision];
  const { eligible, excluded } = splitForBulk(tickets, decision);
  const n = eligible.length;
  const totalNotional = eligible.reduce((sum, o) => {
    const v = effectiveNotional(o);
    return typeof v === "number" && Number.isFinite(v) ? sum + Math.abs(v) : sum;
  }, 0);
  const outcomeById = new Map<number, BulkOutcome>((run?.outcomes || []).map((o): [number, BulkOutcome] => [o.orderId, o]));
  const counts = run ? countOutcomes(run.outcomes) : null;
  const confirmWord = `${copy.confirm} for ${n} ${n === 1 ? "ticket" : "tickets"}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(2, 8, 23, 0.62)" }}
      role="dialog"
      aria-modal="true"
      aria-label={`${copy.title}: ${n} tickets`}
      data-testid="bulk-dialog"
      onClick={running ? undefined : onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl p-5"
        style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--card-border)", boxShadow: "var(--shadow-soft)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={MICRO_LABEL} style={muted}>{copy.title} · bulk</div>
        <h3 className="mt-1 text-[17px] font-semibold">
          {n} {n === 1 ? "ticket" : "tickets"}{totalNotional > 0 ? ` · ${fmtUSD(totalNotional)} notional` : ""}
        </h3>
        <p className="mt-2 text-[12px] leading-5" style={secondary}>
          {copy.blurb} Each ticket is recorded on its own, with this one reason, one after another
          &mdash; a failure on one does not affect the rest. Nothing is sent to the broker &mdash;
          every order is placed by hand in TWS.
        </p>
        {decision === "executed" ? (
          <p className="mt-2 rounded-md px-3 py-2 text-[12px] leading-5" style={{ backgroundColor: "var(--bg-secondary)", ...secondary }}>
            Contracts worked, fill and price are left <strong>blank</strong> (&ldquo;not recorded&rdquo;, never zero).
            IBKR&rsquo;s own fill attaches from the execution feed. If you traded <strong>fewer</strong> than
            recommended on any line, untick it and record that one on its own.
          </p>
        ) : null}

        <div className="mt-3 max-h-[38vh] overflow-y-auto rounded-md border" style={{ borderColor: "var(--card-border)" }}>
          <table className="w-full text-[12px]">
            <caption className="sr-only">Tickets this will record</caption>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em]" style={muted}>
                <th scope="col" className="px-2 py-1.5 font-semibold">Ticket</th>
                <th scope="col" className="px-2 py-1.5 font-semibold">Contract</th>
                <th scope="col" className="px-2 py-1.5 font-semibold">Action</th>
                <th scope="col" className="px-2 py-1.5 font-semibold text-right">Qty</th>
                <th scope="col" className="px-2 py-1.5 font-semibold text-right">Notional</th>
                <th scope="col" className="px-2 py-1.5 font-semibold">{run ? "Result" : "System reason"}</th>
              </tr>
            </thead>
            <tbody>
              {eligible.map((o) => {
                const out = outcomeById.get(o.orderId);
                return (
                  <tr key={o.orderId} className="border-t align-top" style={{ borderColor: "var(--card-border)" }} data-testid={`bulk-row-${o.orderId}`}>
                    <td className="px-2 py-1.5 tabular-nums">#{o.orderId}</td>
                    <td className="px-2 py-1.5">
                      <div className="font-semibold">{contractLabel(o.contract, o.symbol, o.contractId)}</div>
                      <div className="text-[10px]" style={muted}>conid {o.contractId || "—"}</div>
                    </td>
                    <td className="px-2 py-1.5">{o.action || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{numOrDash(effectiveQty(o))}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtUSD(effectiveNotional(o))}</td>
                    <td className="px-2 py-1.5">
                      {out ? (
                        <>
                          <span className={chipClass(BULK_OUTCOME_TONE[out.kind])}>{BULK_OUTCOME_LABEL[out.kind]}</span>
                          {out.text !== "Recorded." ? <div className="mt-0.5 text-[11px]" style={secondary}>{out.text}</div> : null}
                        </>
                      ) : running ? (
                        <span style={muted}>waiting…</span>
                      ) : (
                        <span style={secondary}>{reasonLabel(o.reason) || <span style={muted}>no rationale recorded</span>}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {excluded.length ? (
          <div
            className="mt-3 rounded-md px-3 py-2 text-[12px]"
            data-testid="bulk-excluded"
            style={{ backgroundColor: "rgb(var(--c-warn) / 0.10)", border: "1px solid rgb(var(--c-warn) / 0.34)" }}
          >
            <div className="font-semibold">
              {excluded.length} selected {excluded.length === 1 ? "ticket is" : "tickets are"} left out and stay open:
            </div>
            <ul className="mt-1 space-y-0.5">
              {excluded.map(({ ticket, why: w }) => (
                <li key={ticket.orderId}>
                  #{ticket.orderId} {contractLabel(ticket.contract, ticket.symbol, ticket.contractId)} &mdash; {w}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!run ? (
          <>
            {decision === "deferred" ? (
              <label className="mt-3 block">
                <span className={MICRO_LABEL} style={muted}>Revisit on (all {n})</span>
                <input
                  type="date"
                  value={revisitOn}
                  onChange={(e) => setRevisitOn(e.target.value)}
                  className="mt-1 w-full rounded-md px-3 py-2 text-[13px] tabular-nums"
                  style={{ backgroundColor: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)" }}
                />
                <span className="mt-1 block text-[11px]" style={muted}>
                  {revisitOn ? `Quiet until ${revisitOn}. The tickets stay in the queue.` : "No date: quiet until you decide them. The tickets stay in the queue."}
                </span>
              </label>
            ) : null}
            <label className="mt-3 block">
              <span className={MICRO_LABEL} style={muted}>
                {copy.whyLabel} (one reason, recorded on every ticket) <span style={{ color: "rgb(var(--c-warn))" }}>*</span>
              </span>
              <textarea
                ref={ref}
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                rows={3}
                placeholder={copy.whyPlaceholder}
                aria-label="Reason for all selected tickets"
                className="mt-1 w-full rounded-md px-3 py-2 text-[13px]"
                style={{ backgroundColor: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)" }}
              />
            </label>
            <div className="mt-2 text-[11px]" style={muted}>
              Recorded as {actor || <span style={{ color: "rgb(var(--c-warn))" }}>not signed in</span>}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <HelionButton size="sm" onClick={onClose}>Back</HelionButton>
              <HelionButton
                size="sm"
                data-testid="bulk-confirm"
                onClick={() => onConfirm(why.trim(), decision === "deferred" ? revisitOn.trim() || undefined : undefined)}
                disabled={!actor || !why.trim() || !n}
                className="font-semibold"
                style={{ borderColor: "rgb(var(--c-accent))" }}
              >
                {confirmWord}
              </HelionButton>
            </div>
            {!actor ? (
              <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
                Sign-in required — the ledger must name who decided.
              </div>
            ) : !why.trim() ? (
              <div className="mt-2 text-right text-[11px]" style={{ color: "rgb(var(--c-warn))" }}>
                A reason is required — the ledger must say why.
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="mt-3 text-[12px]" data-testid="bulk-progress" aria-live="polite">
              {running
                ? `Recording ${Math.min(run.outcomes.length + 1, run.total)} of ${run.total}…`
                : `Done: ${counts!.recorded + counts!.recorded_with_problem} of ${run.total} recorded` +
                  (counts!.failed ? ` · ${counts!.failed} failed (still selected; try again)` : "") +
                  (counts!.needs_single ? ` · ${counts!.needs_single} to record on their own` : "") +
                  (counts!.already ? ` · ${counts!.already} already decided` : "") +
                  (counts!.not_attempted ? ` · ${counts!.not_attempted} not sent` : "") +
                  (counts!.recorded_with_problem ? ` · ${counts!.recorded_with_problem} recorded with a problem — read it above` : "")}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              {running ? (
                <HelionButton
                  size="sm"
                  disabled={stopping}
                  onClick={() => { setStopping(true); onStop(); }}
                  title="Finishes the ticket being recorded now, then sends no more."
                >
                  {stopping ? "Stopping after this one…" : "Stop after this one"}
                </HelionButton>
              ) : (
                <HelionButton size="sm" onClick={onClose} data-testid="bulk-close">Close</HelionButton>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ============================================================== lane table */

const LaneTable: React.FC<{
  orders: PendingOrder[];
  emptyMessage: string;
  onDecide: (o: PendingOrder, d: Decision) => void;
  busyId: number | null;
  /** Bulk select. Ticking only selects; nothing is recorded until the bulk dialog is confirmed. */
  selectedIds: number[];
  onSelect: (ids: number[], on: boolean) => void;
  /** True while a bulk run is recording: every button and box in the lane stands still. */
  locked: boolean;
}> = ({ orders, emptyMessage, onDecide, busyId, selectedIds, onSelect, locked }) => {
  if (!orders.length) {
    return <div className="rounded-lg px-3 py-10 text-center text-[13px]" style={muted}>{emptyMessage}</div>;
  }
  const laneIds = orders.map((o) => o.orderId);
  const pickedInLane = laneIds.filter((id) => selectedIds.includes(id));
  const allPicked = pickedInLane.length === laneIds.length;
  const somePicked = pickedInLane.length > 0 && !allPicked;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <caption className="sr-only">Recommendations awaiting a decision</caption>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.14em]" style={muted}>
            <th scope="col" className="pb-2 pr-2 font-semibold">
              <input
                type="checkbox"
                aria-label={allPicked ? "Clear the selection in this lane" : `Select all ${laneIds.length} tickets in this lane`}
                checked={allPicked}
                ref={(el) => { if (el) el.indeterminate = somePicked; }}
                disabled={locked}
                onChange={() => onSelect(laneIds, !allPicked)}
              />
            </th>
            <th scope="col" className="pb-2 pr-3 font-semibold">Contract</th>
            <th scope="col" className="pb-2 pr-3 font-semibold">Action</th>
            <th scope="col" className="pb-2 pr-3 font-semibold text-right">Qty</th>
            <th scope="col" className="pb-2 pr-3 font-semibold text-right">Notional</th>
            <th scope="col" className="pb-2 pr-3 font-semibold text-right">Quote</th>
            <th scope="col" className="pb-2 pr-3 font-semibold">Why the system says so</th>
            <th scope="col" className="pb-2 font-semibold text-right">Decision</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const c = o.contract || undefined;
            const quoteMs = msOf(c?.quoteAsOf);
            const busy = locked || busyId === o.orderId;
            const picked = selectedIds.includes(o.orderId);
            return (
              <tr
                key={o.orderId}
                className="border-t align-top"
                style={{ borderColor: "var(--card-border)", backgroundColor: picked ? "rgb(var(--c-accent) / 0.06)" : undefined }}
              >
                <td className="py-3 pr-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ticket #${o.orderId}`}
                    data-testid={`bulk-select-${o.orderId}`}
                    checked={picked}
                    disabled={locked}
                    onChange={() => onSelect([o.orderId], !picked)}
                  />
                </td>
                <td className="py-3 pr-3">
                  <div className="font-semibold">{contractLabel(c, o.symbol, o.contractId)}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <span className={chipClass(originTone(o.origin))}>{(o.origin || "SYSTEM").toLowerCase()}</span>
                    {c?.dte != null ? <span className="text-[11px]" style={muted}>{c.dte}d to expiry</span> : null}
                    {c?.heldQty != null ? <span className="text-[11px]" style={muted}>· holding {c.heldQty}</span> : null}
                    {!c?.displayName && !c?.underlying ? (
                      <span className={chipClass("neutral")} title="Not in the held-LEAPS book — a new position, or an ETF/alt sleeve ticket">not held</span>
                    ) : null}
                  </div>
                  {/* Contract detail line. The conid is always shown: it is the only
                      identifier guaranteed to be present, and it is what you match
                      against TWS when a label is missing or ambiguous. Strike, expiry
                      and right are shown whenever we hold them — from the held-LEAPS
                      join for existing positions, or from the label staged with the
                      ticket for a new buy the view cannot know about. */}
                  <div className="mt-0.5 text-[10px]" style={muted}>
                    {[
                      c?.strike != null ? `strike ${c.strike.toLocaleString()}` : null,
                      c?.expiration ? `exp ${String(c.expiration).slice(0, 10)}` : null,
                      c?.right ? String(c.right).toUpperCase() : null,
                      c?.multiplier != null ? `×${c.multiplier}` : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                  <div className="mt-0.5 text-[10px]" style={muted}>
                    #{o.orderId} · {fmtLocalDate(o.scheduledDate)} · conid {o.contractId || "—"}
                  </div>
                </td>
                <td className="py-3 pr-3">
                  <span className={chipClass(actionTone(o.action))}>{o.action || "—"}</span>
                </td>
                <td className="py-3 pr-3 text-right tabular-nums">
                  {numOrDash(effectiveQty(o))}
                  {hasOverride(o) ? (
                    <div className="text-[10px] font-semibold" style={{ color: "rgb(var(--c-warn))" }}>
                      override · est {numOrDash(o.estimatedQty)}
                    </div>
                  ) : null}
                </td>
                <td className="py-3 pr-3 text-right tabular-nums">{fmtUSD(effectiveNotional(o))}</td>
                <td className="py-3 pr-3 text-right">
                  {c?.bid != null || c?.ask != null ? (
                    <>
                      <div className="tabular-nums">{px(c?.bid)} / {px(c?.ask)}</div>
                      <div className="mt-0.5 flex justify-end">
                        {/* A quote without its age is how a stale mark gets read as live. */}
                        <FreshnessChip ms={quoteMs} graceSessions={1} asOf={c?.quoteAsOf ?? undefined} />
                      </div>
                    </>
                  ) : (
                    <span style={muted}>—</span>
                  )}
                  <TicketQuoteLine order={o} />
                </td>
                <td className="py-3 pr-3">
                  <div className="max-w-[320px] whitespace-pre-wrap" style={secondary}>
                    {reasonLabel(o.reason) || <span style={muted}>no rationale recorded</span>}
                  </div>
                  {o.tagsData?.ml?.summary ? (
                    <div className="mt-1 max-w-[320px] text-[11px]" style={muted}>{o.tagsData.ml.summary}</div>
                  ) : null}
                </td>
                <td className="py-3 text-right">
                  <div className="inline-flex flex-col items-end gap-1.5">
                    <HelionButton size="sm" disabled={busy} onClick={() => onDecide(o, "executed")}>
                      I traded this
                    </HelionButton>
                    {/* Three ways of not trading it, which used to be one. The ledger
                        keeps them apart: declined and cancelled retire the ticket,
                        deferred leaves it live for another look. */}
                    <div className="inline-flex gap-1.5">
                      <HelionButton
                        size="sm"
                        disabled={busy}
                        onClick={() => onDecide(o, "deferred")}
                        className="opacity-80"
                      >
                        Not yet
                      </HelionButton>
                      <HelionButton
                        size="sm"
                        disabled={busy}
                        onClick={() => onDecide(o, "declined")}
                        className="opacity-80"
                      >
                        Decline
                      </HelionButton>
                      <HelionButton
                        size="sm"
                        disabled={busy}
                        onClick={() => onDecide(o, "cancelled")}
                        className="opacity-60"
                      >
                        Cancel ticket
                      </HelionButton>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ============================================================ round trips */

/* Props annotated explicitly rather than inferred through React.FC. @types/react is
   not installed in this repo, so React.FC resolves to `any` and prop-shape errors go
   unreported -- `tsc --noEmit` stayed clean while this component read two fields that
   had been deleted from RoundTrip. An explicit parameter type is checked regardless.

   `key` is declared only because React.FC normally absorbs it via
   JSX.LibraryManagedAttributes, which is unavailable without @types/react. Installing
   @types/react is the real fix and needs sign-off: it would surface pre-existing
   errors across the app and change the build. */
const RoundTripCard = ({ trip }: { trip: RoundTrip; key?: string | number }) => {
  const [open, setOpen] = useState(false);
  // A negative net on a long-only book is not a short position -- it is a sale whose
  // purchase this ledger never saw, because the sleeve predates the ledger and there
  // is no backfill. Say that, rather than printing "net -2934".
  const soldWithoutBasis = trip.openingLegMissing && trip.netQty < 0;
  // Summed from the reducing legs, NOT from the net: a re-buy between two sales makes
  // the net smaller than what was actually sold, so labelling the net "sold" would
  // understate it. Unpriced legs are excluded because their quantity is unknown.
  const soldQty = trip.entries.reduce(
    (n, e) =>
      String(e.side || "").toUpperCase() !== "BUY" &&
      !e.unpriced &&
      String(e.status || "").toUpperCase() === "EXECUTED"
        ? n + (e.filledQty || 0)
        : n,
    0
  );
  return (
    <div className="rounded-lg" style={{ border: "1px solid var(--card-border)" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-3 px-3.5 py-3 text-left"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{trip.symbol || `conid ${trip.contractId}`}</span>
            {/* A decline-only contract is NOT a position. Reading "open" on it would
                imply a holding that does not exist. */}
            <span
              className={chipClass(
                trip.decisionOnly ? "neutral" : trip.status === "CLOSED" ? "neutral" : "info"
              )}
              title={trip.decisionOnly ? "Considered and declined — nothing was traded" : undefined}
            >
              {trip.decisionOnly ? "not traded" : trip.status.toLowerCase()}
            </span>
            {trip.openingLegMissing ? (
              <span className={chipClass("warning")} title="No opening buy on record — this position predates the ledger, so no cost basis exists for it.">
                no opening leg
              </span>
            ) : null}
            {trip.unpricedCount > 0 ? (
              <span className={chipClass("warning")}>{trip.unpricedCount} awaiting fill</span>
            ) : null}
          </div>
          <div className="mt-0.5 text-[10px]" style={muted}>conid {trip.contractId || "—"}</div>
          <div className="mt-1 text-[11px]" style={muted}>
            {trip.openedOn ? `opened ${String(trip.openedOn).slice(0, 10)}` : "no opening leg on record"}
            {trip.closedOn ? ` · closed ${String(trip.closedOn).slice(0, 10)}` : ""}
            {trip.heldDays != null ? ` · held ${trip.heldDays}d` : ""}
            {` · ${trip.entryCount} ${trip.entryCount === 1 ? "entry" : "entries"}`}
            {soldWithoutBasis
              ? ` · sold ${soldQty || Math.abs(trip.netQty)}`
              : trip.netQty !== 0
                ? ` · net ${trip.netQty}`
                : ""}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {/* No money here, by design. This page records what was traded and why;
              gains and losses come from the broker and the administrator's NAV. */}
          <div className="max-w-[210px] text-[11px] leading-4" style={muted}>
            {trip.positionNote || "state not recorded"}
          </div>
          <div className={`${MICRO_LABEL} mt-1`} style={muted}>
            P&amp;L: IBKR / NAV
            <InfoHint text={trip.pnlAuthority || "Realised gains and losses come from Interactive Brokers and the fund administrator's NAV, not from this ledger."} />
          </div>
        </div>
      </button>

      {open ? (
        <div className="border-t px-3.5 py-3" style={{ borderColor: "var(--card-border)" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em]" style={muted}>
                  <th scope="col" className="pb-1.5 pr-3 font-semibold">Date</th>
                  <th scope="col" className="pb-1.5 pr-3 font-semibold">Action</th>
                  <th scope="col" className="pb-1.5 pr-3 font-semibold text-right">Qty</th>
                  <th scope="col" className="pb-1.5 pr-3 font-semibold text-right">Price</th>
                  <th scope="col" className="pb-1.5 pr-3 font-semibold">Trigger</th>
                  <th scope="col" className="pb-1.5 font-semibold">Why</th>
                </tr>
              </thead>
              <tbody>
                {trip.entries.map((e) => (
                  <tr key={e.ledgerId} className="border-t align-top" style={{ borderColor: "var(--card-border)" }}>
                    <td className="py-1.5 pr-3 tabular-nums">{String(e.entryDate || "").slice(0, 10) || "—"}</td>
                    <td className="py-1.5 pr-3">
                      <span className={chipClass(actionTone(e.action))}>{e.action || e.side || "—"}</span>
                      {e.status && e.status.toUpperCase() !== "EXECUTED" ? (
                        <span className={chipClass("neutral", "ml-1")}>{e.status.toLowerCase()}</span>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {e.unpriced ? <span style={muted}>awaiting</span> : numOrDash(e.filledQty)}
                      {e.followedRecommendation === false ? (
                        <div className="text-[10px]" style={{ color: "rgb(var(--c-warn))" }}>
                          rec {numOrDash(e.recommendedQty)}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {px(e.avgFillPrice)}
                      <FillVsQuoteLine entry={e} />
                    </td>
                    <td className="py-1.5 pr-3" style={muted}>{e.systemTrigger || "—"}</td>
                    <td className="py-1.5" style={secondary}>
                      {e.cioWhy || <span style={muted}>no reason recorded</span>}
                      <DecisionContextDetails ctx={e.decisionContext} />
                      {e.actedBy ? <div className="text-[10px]" style={muted}>{e.actedBy}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Commission only. Cost and proceeds were removed with the derived P&L on
              2026-08-20 and this block was left gated on them, which made the broker's
              commission -- a figure the backend still publishes -- unreachable. Gate on
              the field that exists, so the render cannot be silenced by a field that
              does not. */}
          {trip.commission != null ? (
            <div className="mt-2 flex flex-wrap gap-4 text-[11px]" style={muted}>
              <span>commission {fmtUSD(trip.commission)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* ================================================================== page */

const TradingDesk: React.FC = () => {
  const { userEmail, userLoading } = useUser();
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [ledger, setLedger] = useState<any | null>(null);
  const [liveOrders, setLiveOrders] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  /** When the LEDGER last loaded successfully. Never borrowed from another endpoint. */
  const [ledgerLoadedAt, setLedgerLoadedAt] = useState<string | null>(null);
  // "warning" added 2026-08-31: a scan can complete cleanly AND still have something the
  // CIO must know (lines that cannot produce a ticket at all). That is neither success
  // nor error, and rendering it as either misleads -- green hides it, red implies the
  // run failed.
  const [banner, setBanner] = useState<{ tone: "success" | "warning" | "error"; text: string } | null>(null);
  const [pending, setPending] = useState<{ order: PendingOrder; decision: Decision } | null>(null);
  // Bulk select on the decide lanes. Ids, not rows: the rows are re-read every minute.
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // The open bulk dialog: which decision, and the tickets it was opened on (frozen, so a
  // refresh can never change the list being confirmed).
  const [bulk, setBulk] = useState<{ decision: Decision; tickets: PendingOrder[] } | null>(null);
  const [bulkRun, setBulkRun] = useState<{ total: number; outcomes: BulkOutcome[]; finished: boolean } | null>(null);
  const bulkStopRef = useRef(false);
  // One run at a time, whatever the clicks do; and leaving the page stops sending.
  const bulkRunningRef = useRef(false);
  useEffect(() => () => { bulkStopRef.current = true; }, []);
  // The server's lot-minimum question, keyed to the ticket / selection it was asked about.
  const [decisionLotFlag, setDecisionLotFlag] = useState<{ orderId: number; flag: LotResolutionFlag } | null>(null);
  const [claimLotFlag, setClaimLotFlag] = useState<LotResolutionFlag | null>(null);
  // Fills the CIO has ticked to record as one discretionary trade.
  const [selectedFillIds, setSelectedFillIds] = useState<number[]>([]);
  // A lot-minimum question asked about one selection must never be answered for another.
  useEffect(() => { setClaimLotFlag(null); }, [selectedFillIds]);
  /** Which trades are opened up to show their individual executions. */
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);
  const [claiming, setClaiming] = useState(false);
  const [claimBusy, setClaimBusy] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [lastLoaded, setLastLoaded] = useState<string | null>(null);
  // Controlled so the "reason owed" banner and the blotter's tags can take the CIO
  // straight to where the reason is recorded.
  const [activeTab, setActiveTab] = useState<string>("decide");
  // Set while the backend defers blotter reads for a fill capture; the last blotter stays up.
  const [blotterHeldAt, setBlotterHeldAt] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);
  const liveTimerRef = useRef<number | null>(null);
  // A ref, not only state: the 12-second blotter interval closes over its first render,
  // and must see a capture in flight to stand aside (both read the same IBKR session, and
  // a blotter poll switching sub-account mid-capture empties the feed capture is reading).
  const capturingRef = useRef(false);
  // Auto-capture bookkeeping: attempts per filled order (see blotterFillKey) and when the
  // last automatic capture started. Refs, because they gate an effect, not a render.
  const autoAttemptsRef = useRef<Record<string, number>>({});
  const lastAutoCaptureRef = useRef<number | null>(null);

  const toggleOrder = (key: string) =>
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  /* ------------------------------------------------------------- fetches */

  const fetchOrders = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setOrdersError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/get_pending_orders`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status} – ${res.statusText}`);
      const json = await res.json();
      const rows: PendingOrder[] = Array.isArray(json?.pendingOrders) ? json.pendingOrders : [];
      setOrders(rows.map((r) => ({ ...r, orderId: Number(r.orderId) })).filter((r) => Number.isFinite(r.orderId)));
      setLastLoaded(new Date().toISOString());
    } catch (err: any) {
      console.error("Failed to fetch pending orders", err);
      setOrdersError(err?.message || "Could not load recommendations.");
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  /* Manual re-run of the exit engine. Moved here from the Portfolio page on 2026-08-27
   * (CIO decision Q5): what this produces IS the sell lane below, so the control that
   * re-runs it belongs beside the lane, not on the book-of-record page.
   *
   * This is a RE-RUN, not the only path -- sell_signals_timer fires on weekdays around
   * 06:15-06:35 MT. It stages recommendations only; nothing is transmitted to a broker
   * and every resulting ticket still needs a decision recorded here.
   *
   * The empty case is reported as an outcome ("no new orders staged"), never as silence:
   * a scan that ran and found nothing must not look identical to a scan that failed. */
  const runExitScan = useCallback(async () => {
    if (scanning) return;
    setScanning(true);
    setBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/sell_signals`, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status} - ${res.statusText}`);
      const json = await res.json().catch(() => ({}));

      // The staging gate can return HTTP 200 with an empty order list BEFORE any exit
      // rule is evaluated (strategy/exit_signals.py: `if gate.blocked: return
      // {"exit_orders": [], ...}`). It blocks on weekends, holidays and stale feeds by
      // default. Reporting that as "nothing meets an exit trigger" is the precise
      // false all-clear the backend already documents having cost 14 months of a
      // permanently empty sell lane. Say the engine was blocked instead.
      const gate = json?.meta?.staging_gate ?? json?.meta?.stagingGate ?? null;
      const blocked = gate?.blocked === true || json?.meta?.blocked === true;
      const gateReasons = Array.isArray(gate?.reasons) ? gate.reasons.join("; ") : null;

      // exit_order_count is the GENERATED count and is labelled as such in the backend
      // ("back-compat: this has always been the generated count"). What actually reached
      // the lane is `inserted`. A run that generates 5 and fails to write 3 must not
      // report 5 as "in the lane below".
      const generated = Number(json?.generated ?? json?.exit_order_count ?? json?.count ?? 0);
      const inserted = Number(json?.inserted);
      const failed = Number(json?.failed);
      const skipped = Number(json?.skipped);
      const staged = Number.isFinite(inserted) ? inserted : generated;

      if (blocked) {
        setBanner({
          tone: "error",
          text: `Exit scan was BLOCKED before any rule was evaluated${
            gateReasons ? ` (${gateReasons})` : ""
          }. The lane below was NOT re-evaluated - this is not an all-clear.`,
        });
      } else if (Number.isFinite(failed) && failed > 0) {
        setBanner({
          tone: "error",
          text: `Exit scan generated ${generated} order${generated === 1 ? "" : "s"} but only ${staged} reached the lane - ${failed} failed to save.`,
        });
      } else if (staged > 0) {
        setBanner({
          tone: "success",
          text: `Exit scan staged ${staged} order${staged === 1 ? "" : "s"}. They appear in the sell lane below.`,
        });
      } else {
        // Genuinely evaluated and found nothing. `skipped` means an equivalent ticket is
        // already open, so it is not "nothing staged" -- say which it was.
        //
        // CIO-approved 2026-08-31 (Stage 1). Some held lines CANNOT produce a trim ticket
        // at all: a 50% trim of one contract rounds to zero and the engine only tickets
        // above zero. Ten of 28 lines are in that state, and they are the book's best
        // performers. Without this sentence an empty lane asserts "checked the book,
        // nothing qualified" -- which is false for those lines, in the reassuring
        // direction. The backend computes the count from the live arithmetic
        // (meta.untrimmable_line_count), so it stays true if the trim pct ever moves.
        const untrimmable = Number(json?.meta?.untrimmable_line_count ?? NaN);
        const minForTrim = Number(json?.meta?.min_contracts_for_trim ?? 2);
        const untrimmableNote =
          Number.isFinite(untrimmable) && untrimmable > 0
            ? ` ${untrimmable} position${untrimmable === 1 ? "" : "s"} cannot produce a trim ticket: policy needs at least ${minForTrim} contracts to split a position - they were not evaluated as tradable.`
            : "";
        // Trim once per line (2026-09-17): runners of lines already harvested are not re-offered,
        // and an unreadable harvest record is said, never read as "nothing harvested".
        const harvestedRunners = Array.isArray(json?.meta?.harvest_already_recorded) ? json.meta.harvest_already_recorded.length : 0;
        const harvestedNote =
          harvestedRunners > 0
            ? ` ${harvestedRunners} line${harvestedRunners === 1 ? " is" : "s are"} above 2x but already harvested once - only the runner remains, so no second trim was offered.`
            : "";
        // A ladder group froze: a member is gone with no harvest recorded. It stops trading
        // until that is explained, and this is where the CIO would otherwise not look.
        const frozen = Array.isArray(json?.meta?.cohorts_frozen) ? json.meta.cohorts_frozen : [];
        const frozenNote = frozen.length
          ? ` Ladder group${frozen.length === 1 ? "" : "s"} ${frozen.map((g: any) => g?.key).filter(Boolean).join(", ")} ${
              frozen.length === 1 ? "is" : "are"
            } frozen: a line is no longer held with no harvest recorded, so nothing further is sold from ${
              frozen.length === 1 ? "it" : "them"
            } until that is explained.`
          : "";
        const memoryNote =
          json?.meta?.harvest_memory_unavailable === true
            ? " The harvest record could not be read, so a line harvested before may have been offered a second trim - check before trading one."
            : "";
        setBanner({
          tone: untrimmableNote || memoryNote || frozenNote ? "warning" : "success",
          text:
            (Number.isFinite(skipped) && skipped > 0
              ? `Exit scan ran - no new orders staged; ${skipped} already had an open ticket in the lane.`
              : "Exit scan ran and evaluated the book - nothing meets an exit trigger on the marks it read.") +
            untrimmableNote +
            harvestedNote +
            frozenNote +
            memoryNote,
        });
      }
      await fetchOrders();
    } catch (err: any) {
      console.error("Exit scan failed", err);
      setBanner({
        tone: "error",
        text: `Exit scan failed (${err?.message || "unknown error"}). The lane below is unchanged - it was NOT re-evaluated.`,
      });
    } finally {
      setScanning(false);
    }
  }, [scanning, fetchOrders]);

  const fetchLedger = useCallback(async () => {
    setLedgerError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/trade_ledger?days=1095`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status} – ${res.statusText}`);
      setLedger(await res.json());
      // Stamped HERE, not from `lastLoaded`. That one is written only by fetchOrders,
      // against a different endpoint on the same 60s interval — so when the orders call
      // succeeded and this one failed, a ledger-derived number was dated by a fetch it
      // had nothing to do with. Wrong provenance reads exactly like fresh data.
      setLedgerLoadedAt(new Date().toISOString());
    } catch (err: any) {
      console.error("Failed to fetch the ledger", err);
      setLedgerError(err?.message || "Could not load the ledger.");
    }
  }, []);

  const fetchLiveOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/live_orders`, { cache: "no-store" });
      if (!res.ok) return;
      const j = await res.json();
      if (j && typeof j === "object") {
        // A fill capture (possibly started from another tab or device) holds the IBKR account
        // selection, so the backend read nothing this poll. Keep the last blotter, and say so:
        // an empty payload here is not "no broker orders today".
        if (j.deferred === true || (Array.isArray(j.errors) && j.errors.includes("deferred_for_fill_capture"))) {
          setBlotterHeldAt((prev) => prev ?? new Date().toISOString());
          return;
        }
        setBlotterHeldAt(null);
        setLiveOrders(j);
      }
    } catch {
      /* keep last-good; the feed-state chip reports health separately */
    }
  }, []);

  useEffect(() => {
    if (userLoading) return;
    fetchOrders();
    fetchLedger();
    // Background refresh must not flip the page into a loading state under the
    // cursor while a decision is open.
    timerRef.current = window.setInterval(() => {
      // Also paused while the record dialog is open: refreshing underneath it rewrites
      // the very trade the operator is being asked to confirm. Same reasoning as the
      // pending-ticket pause that was already here.
      if (!pending && !claiming && !bulk) { fetchOrders(true); fetchLedger(); }
    }, 60000);
    return () => { if (timerRef.current) window.clearInterval(timerRef.current); };
    // `claiming` belongs here: the interval closes over it, so without the dependency
    // the callback keeps the value it was created with and the pause never engages.
  }, [fetchOrders, fetchLedger, userLoading, pending, claiming, bulk]);

  useEffect(() => {
    if (userLoading) return;
    fetchLiveOrders();
    liveTimerRef.current = window.setInterval(() => {
      if (document.visibilityState === "visible" && !capturingRef.current) fetchLiveOrders();
    }, 12000);
    return () => { if (liveTimerRef.current) window.clearInterval(liveTimerRef.current); };
  }, [fetchLiveOrders, userLoading]);

  /* ------------------------------------------------------------ decisions */

  const recordDecision = useCallback(async (order: PendingOrder, decision: Decision, why: string, actuals: DecisionActuals = {}) => {
    setBusyId(order.orderId);
    setBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/pending_orders/${order.orderId}/disposition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          decision,
          notes: why,
          actor: userEmail,
          // Omitted entirely when blank so the backend stores NULL ("not recorded")
          // rather than a zero that would read as "sold none".
          ...(actuals.actedQty ? { actedQty: actuals.actedQty } : {}),
          ...(actuals.cioFilledQty ? { cioFilledQty: actuals.cioFilledQty } : {}),
          ...(actuals.cioAvgPrice ? { cioAvgPrice: actuals.cioAvgPrice } : {}),
          ...(actuals.revisitOn ? { revisitOn: actuals.revisitOn } : {}),
          ...(actuals.lotMinimumResolution ? { lotMinimumResolution: actuals.lotMinimumResolution } : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      // The server's lot-minimum question: nothing was written. Keep the dialog open and ask it.
      const lotFlag = res.status === 422 ? readLotResolutionFlag(json) : null;
      if (lotFlag) {
        setDecisionLotFlag({ orderId: order.orderId, flag: lotFlag });
        return;
      }
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);

      const label = contractLabel(order.contract, order.symbol, order.contractId);
      // ONE mapping of the disposition's harvestLock block to words, shared with its tests
      // and pinned against the real handler's bytes (lib/harvestLockout.ts). The branch
      // that used to live here fell through to an empty string for any reason it did not
      // recognise, so a lockout that FAILED TO START rendered as a plain green "recorded".
      const lockNotice = describeDispositionHarvestLock(json?.harvestLock);
      if (lockNotice.tone === "error") {
        // P1-ATOMICITY, 2026-09-04. These are TWO steps, not one: the trade is committed
        // first and the 60-day redeployment lockout is written afterwards. When that write
        // fails the trade IS recorded and the capital is NOT withheld — the exact state
        // the release control is meant to operate on, and it does not exist. The backend
        // also writes HOME_RUN_LOCK_NOT_STARTED to dbo.TradeAudit so it outlives this
        // banner.
        setBanner({ tone: "error", text: `${label} recorded — but ${lockNotice.text}` });
      } else if (json?.ledgerRecorded === false) {
        // The trade IS in the audit trail but did not reach the ledger. Never hide this.
        setBanner({
          tone: "error",
          text: `${label} recorded in the audit trail, but NOT written to the ledger — the ledger table is unavailable. Tell an engineer before trading further.`,
        });
      } else {
        const qtyNote = actuals.actedQty ? ` ${actuals.actedQty} contract(s) recorded.` : "";
        // A home-run trim also starts (or finds) the 60-day redeployment lockout. Say which,
        // with the date: "recorded" that quietly did or did not apply a capital control is
        // exactly the ambiguity this wiring exists to remove.
        // Includes P1-FULLEXIT's "no lockout, and here is why": a full exit of a 2x winner
        // is not a home-run trim, so the proceeds stay in the buy budget and the desk is
        // told rather than left guessing whether a clock started.
        const lockNote = lockNotice.text ? ` ${lockNotice.text}` : "";
        const lotNote = describeRecordedLot(json?.lotMinimum, decisionLotFlag?.flag);
        const successText =
          decision === "executed"
            ? `${label} recorded.${qtyNote}${lockNote}${lotNote} The broker fill will attach from IBKR's feed and will not overwrite anything you typed.`
            : decision === "deferred"
            ? `${label} deferred and recorded. It stays in the queue${actuals.revisitOn ? ` and returns to Act now on ${actuals.revisitOn}` : ""}.`
            : decision === "declined"
            ? `${label} declined and recorded.`
            : `${label} ticket cancelled and recorded.`;
        setBanner({ tone: "success", text: successText });
      }
      setPending(null);
      setDecisionLotFlag(null);
      await Promise.all([fetchOrders(), fetchLedger()]);
    } catch (err: any) {
      console.error(err);
      setBanner({ tone: "error", text: err?.message || "Could not record the decision." });
    } finally {
      setBusyId(null);
    }
  }, [fetchOrders, fetchLedger, userEmail, decisionLotFlag]);

  // A ticket that left the queue (decided here, elsewhere, or by the engine) leaves the
  // selection too, so a bulk dialog can only ever open on tickets that are still live.
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = prev.filter((id) => orders.some((o) => o.orderId === id));
      return next.length === prev.length ? prev : next;
    });
  }, [orders]);

  const selectTickets = useCallback((ids: number[], on: boolean) => {
    setSelectedIds((prev) => (on ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))));
  }, []);

  /* Bulk decisions. ONE request per ticket to the same endpoint the single dialog uses, in
     sequence, so each ticket keeps its own audit and ledger rows and its own 409 guard, and
     one failure cannot undo or block another. Settled tickets leave the selection; failures
     stay selected so they can be tried again. */
  const runBulk = useCallback(async (decision: Decision, tickets: PendingOrder[], why: string, revisitOn?: string, leftOut = 0) => {
    if (!userEmail || bulkRunningRef.current) return;
    bulkRunningRef.current = true;
    bulkStopRef.current = false;
    setBanner(null);
    const outcomes: BulkOutcome[] = [];
    try {
      setBulkRun({ total: tickets.length, outcomes: [], finished: false });
      for (const t of tickets) {
        if (bulkStopRef.current) {
          outcomes.push({ orderId: t.orderId, kind: "not_attempted", text: "Not sent: stopped before this ticket." });
          continue;
        }
        try {
          const res = await fetch(`${API_BASE_URL}/pending_orders/${t.orderId}/disposition`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bulkBody(decision, why, userEmail, revisitOn)),
          });
          const json = await res.json().catch(() => ({}));
          outcomes.push(classifyResponse(t.orderId, res.status, res.ok, json));
        } catch (err: any) {
          // No answer is NOT "not written": the server may have committed before the line
          // dropped. A retired ticket answers 409 on a retry, so that is safe -- but a DEFERRAL
          // leaves the ticket live, and retrying it would write a second deferral row.
          outcomes.push({
            orderId: t.orderId,
            kind: "failed",
            text:
              `No answer from the server (${err?.message || "network error"}). It may or may not have been recorded; ` +
              (decision === "deferred"
                ? "check the ledger before deferring it again, or it may be recorded twice."
                : "trying again is safe (an already-decided ticket is refused, not recorded twice)."),
          });
        }
        setBulkRun({ total: tickets.length, outcomes: [...outcomes], finished: false });
      }
      setBulkRun({ total: tickets.length, outcomes, finished: true });
    } finally {
      bulkRunningRef.current = false;
    }
    const settled = new Set(outcomes.filter((o) => SETTLED_KINDS.includes(o.kind)).map((o) => o.orderId));
    setSelectedIds((prev) => prev.filter((id) => !settled.has(id)));
    setBanner(summarizeOutcomes(decision, outcomes, leftOut));
    await Promise.all([fetchOrders(), fetchLedger()]);
  }, [userEmail, fetchOrders, fetchLedger]);

  const recordDiscretionary = useCallback(async (why: string, action: string, lotMinimumResolution?: LotResolution) => {
    setClaimBusy(true);
    setBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/record_discretionary_trade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fillIds: selectedFillIds,
          reason: why,
          action,
          actor: userEmail,
          ...(lotMinimumResolution ? { lotMinimumResolution } : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      const lotFlag = res.status === 422 ? readLotResolutionFlag(json) : null;
      if (lotFlag) {
        setClaimLotFlag(lotFlag);
        return;
      }
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setBanner({
        tone: "success",
        text: `${json?.symbol || json?.contractId}: ${json?.fillsAttached} execution(s) recorded as a ${json?.action} of ${json?.actedQty}.${describeRecordedLot(json?.lotMinimum, claimLotFlag)} The reason is now on the ledger.`,
      });
      setClaiming(false);
      setClaimLotFlag(null);
      setSelectedFillIds([]);
      await Promise.all([fetchOrders(), fetchLedger()]);
    } catch (err: any) {
      console.error(err);
      setBanner({ tone: "error", text: err?.message || "Could not record the trade." });
    } finally {
      setClaimBusy(false);
    }
  }, [selectedFillIds, userEmail, fetchOrders, fetchLedger, claimLotFlag]);

  /* Reads IBKR's execution feed into the ledger's evidence table. `auto` marks a capture
   * the page started itself because the blotter showed a fill the ledger does not hold.
   *
   * Until 2026-09-16 this printed a green "Broker feed read." for any 200 -- including
   * the weeks when the feed returned nothing and no trade reached the ledger. The words
   * now come from what the run could READ (describeCapture), not the status code. */
  const runCapture = useCallback(async (auto = false) => {
    if (capturingRef.current) return;
    capturingRef.current = true;
    setCapturing(true);
    if (!auto) setBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/capture_fills?lookback=7`, { cache: "no-store" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      const outcome = describeCapture(json);
      // An automatic run that read the feed and found nothing new stays quiet. Anything
      // else is said: it is either news (new executions) or a failure.
      if (!auto || outcome.tone !== "success" || Number(json?.captured) > 0) {
        setBanner({
          tone: outcome.tone,
          text: (auto ? "Automatic fill capture (the blotter showed a fill the ledger did not hold): " : "") + outcome.text,
        });
      }
      await Promise.all([fetchLedger(), fetchLiveOrders()]);
    } catch (err: any) {
      setBanner({
        tone: "error",
        text:
          `${auto ? "Automatic fill capture failed" : "Could not read the broker feed"} ` +
          `(${err?.message || "unknown error"}). Trades on the blotter may be missing from the ledger.`,
      });
    } finally {
      capturingRef.current = false;
      setCapturing(false);
    }
  }, [fetchLedger, fetchLiveOrders]);

  /* --------------------------------------------------------------- derive */

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) =>
      [contractLabel(o.contract, o.symbol, o.contractId), o.contractId, o.reason, o.action, String(o.orderId)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [orders, search]);

  // Lanes split by DIRECTION, which is what determines what you do in TWS.
  // Origin is shown as a chip inside each row rather than splitting on it, so a
  // discretionary sell and a signal-driven sell sit in the same worklist.
  const sellLane = useMemo(
    () => filtered.filter((o) => (o.action || "").toUpperCase() !== "BUY"),
    [filtered]
  );
  const buyLane = useMemo(
    () => filtered.filter((o) => (o.action || "").toUpperCase() === "BUY"),
    [filtered]
  );

  // Bulk select. The selection is held against ALL open tickets, not the search, so it
  // says how many selected tickets the search is hiding rather than acting on them unseen.
  const selectedOrders = useMemo(() => orders.filter((o) => selectedIds.includes(o.orderId)), [orders, selectedIds]);
  const hiddenSelected = selectedOrders.filter((o) => !filtered.some((f) => f.orderId === o.orderId)).length;
  const sameReasonGroups = useMemo(() => reasonGroups(orders), [orders]);
  const bulkLocked = !!bulkRun && !bulkRun.finished;
  const openBulk = (decision: Decision) => {
    setBulkRun(null);
    setBulk({ decision, tickets: selectedOrders });
  };

  const roundTrips: RoundTrip[] = ledger?.roundTrips || [];
  // Memoised for its IDENTITY, not its cost. `ledger?.unmatchedFills || []` produces a
  // fresh array every render whenever the key is absent, which would re-run the grouping
  // on every render and hand React new group objects each time.
  const unmatchedFills: UnmatchedFill[] = useMemo(
    () => ledger?.unmatchedFills || [],
    [ledger]
  );

  // The executions TWS split each order into, gathered back into the trades the CIO
  // actually made. See groupFills for why the key is derived rather than taken from a
  // broker order id (there isn't one in the executions feed).
  const { groups: fillGroups, ungroupable: ungroupableFills } = useMemo(
    () => groupFills(unmatchedFills),
    [unmatchedFills]
  );

  // The selection is a VIEW of the fetched fills, never independent state. The 60s
  // background refresh runs while the modal is open, so without this the modal could
  // describe one set of executions while the POST submitted another -- including ids
  // another writer had already claimed. Anything no longer in the payload is dropped.
  useEffect(() => {
    setSelectedFillIds((prev) => {
      const live = prev.filter((id) => unmatchedFills.some((f) => f.fillId === id));
      if (live.length === prev.length) return prev;
      // A dialog that disappears while you are typing into it is the worst version of
      // this: the reason is destroyed, nothing says why, and the flag stayed true so the
      // next row click reopened it unbidden. Close it deliberately and SAY what happened.
      if (prev.length && !live.length) {
        setClaiming(false);
        setBanner({
          tone: "error",
          text:
            "The executions you were recording are no longer in the break queue — they were " +
            "claimed or matched to a ledger entry while the dialog was open. Nothing was recorded.",
        });
      }
      return live;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger]);
  // THREE states, not two. "Still loading" is not "failed", and conflating them made
  // the page assert "could not read the queue" on every single load -- a warning that
  // cries wolf is a warning the operator learns to scroll past, which costs more than
  // having none. A missing unmatchedFillsAvailable flag means an OLDER backend that
  // always returned rows, so its absence must NOT read as unavailable.
  const ledgerLoading = ledger == null && ledgerError == null;
  // Applies to EVERY number derived from this payload, not just the break queue: a
  // failed load leaves "Awaiting fill price" and "Positions tracked" equally unfounded.
  // ledgerAvailable === false is the backend's own "the table does not exist" exit — a
  // 200 response carrying nothing. Leaving it out of this meant a database with no
  // ledger table rendered "all recorded trades priced", which is the same lie as a
  // failed fetch wearing a success code.
  const ledgerUnavailable =
    !ledgerLoading &&
    (ledgerError != null || ledger == null || ledger.ledgerAvailable === false);
  const queueUnavailable =
    ledgerUnavailable || (ledger != null && ledger.unmatchedFillsAvailable === false);
  // The backend's true count; falls back to the rows we were given.
  // Never below what is actually on screen. COUNT(*) and the capped SELECT are separate
  // statements on one cursor, so the count can legitimately trail the rows — and a count
  // of 0 above three visible unclaimed fills would print "every fill is accounted for"
  // over the evidence that it is not.
  const unmatchedCount: number = Math.max(
    typeof ledger?.counts?.unmatchedFills === "number" ? ledger.counts.unmatchedFills : 0,
    unmatchedFills.length
  );
  // The backend ships at most a display cap and reports the true total separately, so
  // silent truncation has to be said out loud rather than left to look like the whole.
  const shownCount: number =
    typeof ledger?.unmatchedFillsShown === "number"
      ? ledger.unmatchedFillsShown
      : unmatchedFills.length;
  const truncated = shownCount < unmatchedCount;
  const unpricedCount = ledger?.counts?.unpriced ?? 0;
  const feedDown = isLiveFeedDown(liveOrders);

  /* ------------------------------------------- blotter ↔ ledger reconciliation
   * Every FILLED order on the blotter is checked against the ledger (2026-09-16). The
   * blotter reads IBKR's orders feed and the ledger its executions feed; when capture
   * silently stored nothing, the FI rebalance sat on the blotter while every ledger
   * surface read "all clear". A trade the broker shows and the ledger does not hold is
   * now stated on the row, counted in the banner, and triggers a capture. */
  const ledgerReadable = !ledgerLoading && !queueUnavailable;
  const openTickets = useMemo(
    () => orders.map((o) => ({ orderId: o.orderId, contractId: o.contractId, action: o.action, symbol: o.symbol })),
    [orders]
  );
  const blotterRecon = useMemo(() => {
    const rows: any[] = Array.isArray(liveOrders?.working_orders) ? liveOrders.working_orders : [];
    const entries: LedgerEntry[] = Array.isArray(ledger?.entries) ? ledger.entries : [];
    const byKey = new Map<string, BlotterLedgerStatus>();
    const notCapturedKeys: string[] = [];
    for (const o of rows) {
      const status = reconcileBlotterOrder(o, {
        groups: fillGroups,
        entries,
        tickets: openTickets,
        ledgerReadable,
        asOfUtc: liveOrders?.as_of_utc ?? null,
      });
      if (!status) continue;
      const key = blotterFillKey(o);
      byKey.set(key, status);
      if (status.kind === "not_captured") notCapturedKeys.push(key);
    }
    return { byKey, notCapturedKeys };
  }, [liveOrders, ledger, fillGroups, openTickets, ledgerReadable]);

  // Capture as soon as the blotter shows a fill the ledger does not hold, so the reason is
  // asked for while the CIO is still at the desk rather than after the 15:45 MT timer.
  // Spaced and budgeted in shouldAutoCapture; the timer remains the backstop for trades
  // placed while this page is closed.
  useEffect(() => {
    const now = Date.now();
    if (
      !shouldAutoCapture({
        notCapturedKeys: blotterRecon.notCapturedKeys,
        attempts: autoAttemptsRef.current,
        lastAutoCaptureAt: lastAutoCaptureRef.current,
        now,
        capturing: capturingRef.current,
        ledgerReadable,
      })
    ) {
      return;
    }
    lastAutoCaptureRef.current = now;
    for (const k of blotterRecon.notCapturedKeys) {
      autoAttemptsRef.current[k] = (autoAttemptsRef.current[k] || 0) + 1;
    }
    runCapture(true);
  }, [blotterRecon, ledgerReadable, runCapture]);

  // What is owed a reason, and where it is recorded. A trade with an open ticket is
  // recorded from the ticket on Decide; recording it from the queue as well would put it on
  // the ledger twice.
  const owed = useMemo(() => {
    const ticketed = fillGroups.filter((g) => findOpenTicket(openTickets, g.contractId, g.symbol, g.direction));
    return {
      queueTrades: fillGroups.length - ticketed.length,
      ticketTrades: ticketed.length,
      notCaptured: blotterRecon.notCapturedKeys.length,
      notCapturedExhausted: blotterRecon.notCapturedKeys.filter(
        (k) => (autoAttemptsRef.current[k] || 0) >= AUTO_CAPTURE_MAX_ATTEMPTS
      ).length,
    };
  }, [fillGroups, openTickets, blotterRecon]);
  const owedTotal = owed.queueTrades + owed.ticketTrades + owed.notCaptured;

  const goToQueue = useCallback(() => {
    setActiveTab("ledger");
    window.setTimeout(() => {
      document.getElementById("ledger-break-queue")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  /* ---------------------------------------------------------------- render */

  return (
    <Layout>
      <div className="mx-auto max-w-[1880px] space-y-4 px-3 py-3 text-[13px] sm:px-4 xl:px-5 2xl:text-[14px]">

        {/* ---- header ---- */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className={MICRO_LABEL} style={muted}>Trading Desk</p>
            <h1 className="mt-1 text-[2.05rem] font-semibold leading-none">The Trade Ledger</h1>
            <p className="mt-2 max-w-[70ch] text-[13px]" style={secondary}>
              What we traded, why, and at what price. Execution happens in TWS —{" "}
              <strong>nothing on this page transmits an order</strong>. Record a trade here and the
              broker&rsquo;s own execution feed attaches the price.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[220px]">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contract, reason, ticket…" />
            </div>
            <HelionButton size="sm" onClick={() => runCapture(false)} disabled={capturing}>
              {capturing ? "Reading broker…" : "Sync fills"}
            </HelionButton>
            <HelionButton size="sm" onClick={() => { fetchOrders(); fetchLedger(); }} disabled={loading || userLoading}>
              {loading ? "Refreshing…" : "Refresh"}
            </HelionButton>
          </div>
        </div>

        {banner ? (
          <div
            className="rounded-lg px-4 py-3 text-[13px]"
            style={{
              backgroundColor:
                banner.tone === "success"
                  ? "rgb(var(--c-up) / 0.10)"
                  : banner.tone === "warning"
                  ? "rgba(245, 158, 11, 0.12)"
                  : "rgb(var(--c-down) / 0.10)",
              border: `1px solid ${
                banner.tone === "success"
                  ? "rgb(var(--c-up) / 0.32)"
                  : banner.tone === "warning"
                  ? "rgba(245, 158, 11, 0.34)"
                  : "rgb(var(--c-down) / 0.32)"
              }`,
            }}
          >
            {banner.text}
          </div>
        ) : null}

        {/* ---- state band ---- */}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label="To decide"
            // Fed by get_pending_orders, so it carries the ORDERS timestamp and the
            // ORDERS failure state -- the same treatment its ledger-fed neighbours get,
            // from the fetch that actually earned it. Before this, `lastLoaded` was
            // written by fetchOrders and then read by the ledger tile, which dated a
            // ledger number with an orders fetch.
            value={ordersError ? "—" : sellLane.length + buyLane.length}
            valueClassName={ordersError ? "text-warn" : ""}
            subtext={
              ordersError
                ? "could not read recommendations — unknown, not clear"
                : `${sellLane.length} sell · ${buyLane.length} buy` +
                  (lastLoaded ? ` · loaded ${fmtLocalDateTime(lastLoaded)}` : "")
            }
          />
          <StatTile
            label="Awaiting fill price"
            value={ledgerLoading ? "…" : ledgerUnavailable ? "—" : unpricedCount}
            hint="Recorded trades whose broker execution has not reached us yet. They stay unpriced rather than showing a guessed number."
            subtext={
              ledgerLoading
                ? "reading…"
                : ledgerUnavailable
                  ? "could not read the ledger — unknown, not clear"
                  : unpricedCount
                    ? "broker feed has not confirmed"
                    : "all recorded trades priced"
            }
            valueClassName={ledgerUnavailable ? "text-warn" : unpricedCount ? "text-warn" : ""}
          />
          <StatTile
            label="Fills with no entry"
            // The TRUE count from the backend, not the number of rows shipped: the
            // display cap used to saturate this, so a busy session could never report
            // more unrecorded trades than the cap.
            value={ledgerLoading ? "…" : queueUnavailable ? "—" : unmatchedCount}
            hint="Broker executions we captured but could not attribute to a ledger entry — a trade with no recorded reason."
            // "every fill is accounted for" must NEVER be shown for a queue that could
            // not be read. Until 2026-08-20 a failed query and a clean book produced
            // byte-identical output, so the failure surfaced as reassurance.
            subtext={
              ledgerLoading
                ? "reading…"
                : queueUnavailable
                  ? "could not read the queue — unknown, not clear"
                  : unmatchedCount
                    ? "needs a reason recorded"
                    : "every fill is accounted for"
            }
            valueClassName={queueUnavailable ? "text-warn" : unmatchedCount ? "text-down" : ""}
          />
          <StatTile
            label="Positions tracked"
            // POSITIONS ONLY. A contract that was only ever declined is a recorded
            // decision, not a holding, and counting it here would assert a position
            // that does not exist.
            //
            // Guarded like its two neighbours. Unguarded, this tile printed a confident
            // "0" on first load while they honestly said "reading…", and kept printing
            // the last-good count in the normal colour after a failed refresh had turned
            // both of them into em dashes — three tiles fed by ONE payload, disagreeing
            // about whether that payload could be read.
            value={
              ledgerLoading ? "…"
                : ledgerUnavailable ? "—"
                  : roundTrips.filter((t) => !t.decisionOnly).length
            }
            valueClassName={ledgerUnavailable ? "text-warn" : ""}
            hint="Contracts with at least one executed trade. Contracts you were shown and declined are recorded in the ledger but are not positions."
            subtext={
              ledgerLoading
                ? "reading…"
                : ledgerUnavailable
                  ? "could not read the ledger — unknown, not clear"
                  : (() => {
                      const declined = roundTrips.filter((t) => t.decisionOnly).length;
                      // ledgerLoadedAt, not lastLoaded: this number came from the ledger,
                      // so it must be dated by the ledger's own fetch.
                      const loaded = ledgerLoadedAt ? `loaded ${fmtLocalDateTime(ledgerLoadedAt)}` : "—";
                      return declined ? `${declined} declined, not counted · ${loaded}` : loaded;
                    })()
            }
          />
        </div>

        {ordersError ? (
          <div className="rounded-lg px-4 py-3 text-[13px]"
               style={{ backgroundColor: "rgb(var(--c-down) / 0.10)", border: "1px solid rgb(var(--c-down) / 0.32)" }}>
            Recommendations could not be loaded — {ordersError}. This list may be incomplete.
          </div>
        ) : null}

        {/* ---- reasons owed (2026-09-16) ----
            Every executed trade gets a reason. The break queue used to live only on the
            Ledger tab, behind a page that opens on Decide, so an unrecorded trade could sit
            there unseen -- and when capture stored nothing, there was not even that. This
            is on every tab, counts trades still on the blotter that the ledger has not
            received, and goes straight to where the reason is recorded. */}
        {ledgerReadable && owedTotal > 0 ? (
          <div
            role="alert"
            data-testid="reasons-owed-banner"
            className="flex flex-col gap-2 rounded-lg px-4 py-3 text-[13px] lg:flex-row lg:items-center lg:justify-between"
            style={{ backgroundColor: "rgb(var(--c-down) / 0.10)", border: "1px solid rgb(var(--c-down) / 0.40)" }}
          >
            <div>
              <div className="font-semibold">
                {owedTotal} {owedTotal === 1 ? "trade" : "trades"} executed with no reason recorded
              </div>
              <div className="mt-0.5 text-[12px]" style={secondary}>
                {[
                  owed.queueTrades
                    ? `${owed.queueTrades} in the Ledger queue`
                    : null,
                  owed.ticketTrades
                    ? `${owed.ticketTrades} with an open ticket: record ${owed.ticketTrades === 1 ? "it" : "them"} on Decide, not from the queue`
                    : null,
                  owed.notCaptured
                    ? `${owed.notCaptured} filled on the blotter today and not in the ledger yet` +
                      (capturing
                        ? " (reading IBKR's execution feed now)"
                        : owed.notCapturedExhausted
                          ? ` (automatic capture tried ${AUTO_CAPTURE_MAX_ATTEMPTS} times; press Sync fills)`
                          : " (capture queued)")
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {owed.queueTrades ? (
                <HelionButton size="sm" onClick={goToQueue}>Record why</HelionButton>
              ) : null}
              {owed.ticketTrades ? (
                <HelionButton size="sm" onClick={() => setActiveTab("decide")}>Open Decide</HelionButton>
              ) : null}
              {owed.notCaptured && !owed.queueTrades && !owed.ticketTrades ? (
                <HelionButton size="sm" onClick={() => runCapture(false)} disabled={capturing}>
                  {capturing ? "Reading broker…" : "Sync fills"}
                </HelionButton>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* ---- tabs ---- */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="decide">Decide ({sellLane.length + buyLane.length})</TabsTrigger>
            {/* Same unguarded source as the tile: a stale or zero count here reads as
                fact. Show nothing rather than a number we cannot stand behind. */}
            <TabsTrigger value="ledger">
              Ledger{ledgerLoading || ledgerUnavailable ? "" : ` (${roundTrips.length})`}
            </TabsTrigger>
            <TabsTrigger value="live">Live blotter</TabsTrigger>
          </TabsList>

          {/* ============================ DECIDE ============================ */}
          <TabsContent value="decide" className="space-y-4">
            {/* ---- bulk select (CIO 2026-11-04) ----
                Selecting records nothing. Every action opens a dialog that lists each ticket,
                asks for the one shared reason, and records ticket by ticket. */}
            {sameReasonGroups.length || selectedIds.length ? (
              <div
                data-testid="bulk-bar"
                className="sticky top-2 z-10 rounded-lg px-4 py-3 text-[12px]"
                style={{
                  backgroundColor: "var(--card-bg)",
                  border: selectedIds.length ? "1px solid rgb(var(--c-accent) / 0.55)" : "1px solid var(--card-border)",
                  boxShadow: selectedIds.length ? "var(--shadow-soft)" : undefined,
                }}
              >
                {sameReasonGroups.length ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={MICRO_LABEL} style={muted}>Same reason</span>
                    {sameReasonGroups.map((g) => (
                      <HelionButton
                        key={g.reason}
                        size="sm"
                        disabled={bulkLocked}
                        onClick={() => setSelectedIds(g.ids)}
                        title="Replaces the current selection with every open ticket carrying this system reason. Records nothing."
                      >
                        Select all {g.ids.length} · {reasonLabel(g.reason)}
                      </HelionButton>
                    ))}
                  </div>
                ) : null}
                {selectedIds.length ? (
                  <div className={`flex flex-wrap items-center gap-2 ${sameReasonGroups.length ? "mt-2" : ""}`}>
                    <span className="font-semibold" data-testid="bulk-count">
                      {selectedIds.length} {selectedIds.length === 1 ? "ticket" : "tickets"} selected
                    </span>
                    {hiddenSelected ? (
                      <span className={chipClass("warning")}>{hiddenSelected} hidden by the search</span>
                    ) : null}
                    <span className="flex-1" />
                    {(() => {
                      const n = splitForBulk(selectedOrders, "executed").eligible.length;
                      return (
                        <HelionButton
                          size="sm"
                          disabled={bulkLocked || !n}
                          onClick={() => openBulk("executed")}
                          title={n ? "Record the selected plain sells as traded in TWS. Buys and home-run harvests are left for their own dialog." : "None of the selected tickets is a plain sell. Buys and home-run harvests are recorded one at a time."}
                        >
                          I traded these{n !== selectedIds.length ? ` (${n} of ${selectedIds.length})` : ""}
                        </HelionButton>
                      );
                    })()}
                    <HelionButton size="sm" disabled={bulkLocked} onClick={() => openBulk("deferred")} className="opacity-80">Not yet</HelionButton>
                    <HelionButton size="sm" disabled={bulkLocked} onClick={() => openBulk("declined")} className="opacity-80">Decline</HelionButton>
                    <HelionButton size="sm" disabled={bulkLocked} onClick={() => openBulk("cancelled")} className="opacity-60">Cancel tickets</HelionButton>
                    <HelionButton size="sm" disabled={bulkLocked} onClick={() => setSelectedIds([])} className="opacity-60">Clear</HelionButton>
                  </div>
                ) : null}
              </div>
            ) : null}

            <SectionCard
              title="Sell — what the system says to reduce"
              subtitle="Regime signals, exit hints, DTE breaches and home-run trims from the exit engine, plus anything you send over from the Exit / Harvest Watchlist. Trade it in TWS, then record it here with your reason. The engine runs itself on weekdays; Run exit scan re-evaluates the book now."
              right={
                <div className="flex items-center gap-2">
                  <span className={chipClass(sellLane.length ? "warning" : "neutral")}>{sellLane.length} open</span>
                  {/* Re-runs the exit engine and re-reads this lane. Staging only -- it
                      transmits nothing to a broker, and every ticket it produces still
                      needs a decision recorded below. */}
                  <HelionButton
                    size="sm"
                    disabled={scanning}
                    onClick={runExitScan}
                    title="Re-run the exit engine now and reload this lane. Stages recommendations only - nothing is sent to a broker."
                  >
                    {scanning ? "Scanning…" : "Run exit scan"}
                  </HelionButton>
                </div>
              }
            >
              <LaneTable
                orders={sellLane}
                emptyMessage={
                  ordersError
                    ? "Could not load — see the error above."
                    : loading
                    ? "Loading…"
                    : search
                    ? "No sell recommendations match that search."
                    : "No sell ticket is open. Recommendations arrive from the exit engine on weekdays, or you can send one yourself from the Risk dashboard's Exit / Harvest Watchlist. An empty lane means no open TICKET — it is not a statement that nothing is flagged."
                }
                onDecide={(o, d) => setPending({ order: o, decision: d })}
                busyId={busyId}
                selectedIds={selectedIds}
                onSelect={selectTickets}
                locked={bulkLocked}
              />
            </SectionCard>

            <SectionCard
              title="Buy — capital you chose to put to work"
              subtitle="Candidates staged from the Screener, plus anything you initiated yourself."
              right={<span className={chipClass(buyLane.length ? "positive" : "neutral")}>{buyLane.length} open</span>}
            >
              <LaneTable
                orders={buyLane}
                emptyMessage={
                  ordersError
                    ? "Could not load — see the error above."
                    : loading
                    ? "Loading…"
                    : "No buy ticket is open. Stage candidates from the Options Screener. An empty lane means no open TICKET, not that nothing qualifies."
                }
                onDecide={(o, d) => setPending({ order: o, decision: d })}
                busyId={busyId}
                selectedIds={selectedIds}
                onSelect={selectTickets}
                locked={bulkLocked}
              />
            </SectionCard>
          </TabsContent>

          {/* ============================ LEDGER ============================ */}
          <TabsContent value="ledger" className="space-y-4">
            {/* A KNOWN hole in the record, stated where the record is read. Fill capture
                stored nothing from 2026-08-21 (IBKR's execution feed answered empty after
                every account switch) and the feed only reaches back 7 days, so trades in
                these dates cannot be recovered from it. CIO decision 2026-09-16: say so
                here, and scope a backfill from another source for approval. */}
            {LEDGER_CAPTURE_GAP ? (
              <div
                className="rounded-lg px-4 py-3 text-[12px]"
                style={{ backgroundColor: "rgb(var(--c-warn) / 0.10)", border: "1px solid rgb(var(--c-warn) / 0.34)" }}
              >
                <span className="font-semibold">
                  Capture gap: {fmtCalendarDate(LEDGER_CAPTURE_GAP.from)} to {fmtCalendarDate(LEDGER_CAPTURE_GAP.to)}.
                </span>{" "}
                Broker executions in these dates never reached this ledger: a fill-capture defect, fixed
                16 Sep 2026. Any trade made then is missing from the queue and the round-trips below, and
                IBKR&rsquo;s execution feed no longer holds it. A backfill from another source is being
                scoped; nothing will be loaded without your approval.
              </div>
            ) : null}
            {/* The TRUE count, not the row count. The backend runs COUNT(*) and the
                capped SELECT as separate statements, so a fill claimed between them
                gives count>0 with zero rows -- and the tile would assert unrecorded
                trades while this tab offered nowhere to find them. */}
            {unmatchedFills.length || unmatchedCount > 0 || queueUnavailable ? (
              <div id="ledger-break-queue" className="scroll-mt-4">
              <SectionCard
                title="Fills with no reason recorded"
                subtitle={
                  queueUnavailable
                    ? "The break queue could not be read. This is an UNKNOWN, not an all-clear — there may be trades here with no recorded reason."
                    : truncated
                      ? `Showing ${shownCount} of ${unmatchedCount} executions. The broker executed these but no ledger entry claims them.`
                      : `${fillGroups.length} ${fillGroups.length === 1 ? "trade" : "trades"} across ` +
                        `${unmatchedFills.length - ungroupableFills.length} ` +
                        `${unmatchedFills.length - ungroupableFills.length === 1 ? "execution" : "executions"}` +
                        (ungroupableFills.length
                          ? `, plus ${ungroupableFills.length} non-directional record` +
                            `${ungroupableFills.length === 1 ? "" : "s"} that cannot be recorded here`
                          : "") +
                        ". TWS splits one order into several fills; these are gathered back into the " +
                        "trades you made. Two separate orders on one ticker in one session appear as " +
                        "one row — expand it to split them. Pick one, then record why."
                }
                right={
                  <div className="flex items-center gap-2">
                    {/* Not actionable while the queue is unreadable. These rows are the
                        last-known set, not the current one, and recording a trade from
                        rows we have just told the operator we could not read would be
                        acting on evidence we disclaimed in the same breath. */}
                    <HelionButton
                      size="sm"
                      disabled={!selectedFillIds.length || queueUnavailable}
                      onClick={() => setClaiming(true)}
                    >
                      {selectedFillIds.length ? "Record this trade" : "Record a trade"}
                    </HelionButton>
                    {/* The TRUE count, matching the tile. Showing the capped row count
                        here let the two disagree with no explanation. */}
                    {/* Counts what cannot be truncated away. Group counts come from a
                        capped payload, and a queue holding only non-directional records
                        would otherwise read "0 trades" while visibly holding rows. */}
                    <span className={chipClass(queueUnavailable ? "warning" : "danger")}>
                      {queueUnavailable
                        ? "unknown"
                        : truncated
                          ? `${unmatchedCount} executions`
                          : `${fillGroups.length} ${fillGroups.length === 1 ? "trade" : "trades"}` +
                            (ungroupableFills.length ? ` + ${ungroupableFills.length}` : "")}
                    </span>
                  </div>
                }
              >
                {queueUnavailable && !unmatchedFills.length ? (
                  <div className="rounded-lg px-3 py-8 text-center text-[13px]" style={{ color: "rgb(var(--c-warn))" }}>
                    Could not read the break queue &mdash; unknown, not clear.
                    {ledgerError ? <div className="mt-1 text-[11px]" style={muted}>{ledgerError}</div> : null}
                  </div>
                ) : (
                <>
                {/* Rows survive a failed refresh so the operator does not lose their place,
                    but they must not masquerade as current. Dated, and inert. */}
                {queueUnavailable ? (
                  <div
                    className="mb-2 rounded-md px-3 py-2 text-[12px]"
                    style={{ backgroundColor: "rgb(var(--c-warn) / 0.10)", border: "1px solid rgb(var(--c-warn) / 0.32)" }}
                  >
                    Showing the last set read
                    {ledgerLoadedAt ? ` at ${fmtLocalDateTime(ledgerLoadedAt)}` : ""}. The queue could
                    not be re-read, so this may no longer be complete &mdash; recording is disabled
                    until it loads.
                  </div>
                ) : null}
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <caption className="sr-only">
                      Broker executions with no ledger entry, grouped into the trades they came from
                    </caption>
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em]" style={muted}>
                        <th scope="col" className="pb-1.5 pr-2 font-semibold">
                          <span className="sr-only">Select</span>
                        </th>
                        <th scope="col" className="pb-1.5 pr-2 font-semibold">
                          <span className="sr-only">Expand</span>
                        </th>
                        <th scope="col" className="pb-1.5 pr-3 font-semibold">Instrument</th>
                        <th scope="col" className="pb-1.5 pr-3 font-semibold">Side</th>
                        <th scope="col" className="pb-1.5 pr-3 font-semibold text-right">Quantity</th>
                        <th scope="col" className="pb-1.5 pr-3 font-semibold text-right">Avg price</th>
                        <th scope="col" className="pb-1.5 pr-3 font-semibold text-right">Notional</th>
                        <th scope="col" className="pb-1.5 font-semibold text-right">Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fillGroups.map((g) => {
                        const ids = g.fills.map((f) => f.fillId);
                        const picked = ids.filter((id) => selectedFillIds.includes(id));
                        const all = picked.length === ids.length && ids.length > 0;
                        const some = picked.length > 0 && !all;
                        const open = openGroups.includes(g.key);
                        // EXCLUSIVE. record_discretionary_trade takes one contract per
                        // request, so selecting a second trade must replace the first
                        // rather than build a mixed set the endpoint would refuse. One
                        // reason across several tickers needs the atomic batch write.
                        const selectGroup = () =>
                          setSelectedFillIds(all ? [] : ids);
                        return (
                          <React.Fragment key={g.key}>
                            <tr
                              className={`border-t ${queueUnavailable ? "" : "cursor-pointer"}`}
                              style={{
                                borderColor: "var(--card-border)",
                                backgroundColor: picked.length ? "rgb(var(--c-accent) / 0.06)" : undefined,
                                opacity: queueUnavailable ? 0.6 : undefined,
                              }}
                              onClick={queueUnavailable ? undefined : selectGroup}
                            >
                              <td className="py-2 pr-2">
                                <input
                                  type="checkbox"
                                  aria-label={`Select the ${g.direction} of ${g.totalQty} ${unitWord(g.secType, g.totalQty)} of ${g.symbol}`}
                                  disabled={queueUnavailable}
                                  checked={all}
                                  ref={(el) => { if (el) el.indeterminate = some; }}
                                  onChange={selectGroup}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="py-2 pr-2">
                                <button
                                  type="button"
                                  aria-expanded={open}
                                  aria-label={open ? `Hide the ${g.fills.length} executions` : `Show the ${g.fills.length} executions`}
                                  onClick={(e) => { e.stopPropagation(); toggleGroup(g.key); }}
                                  className="px-1 text-[11px]"
                                  style={muted}
                                >
                                  {open ? "\u25BE" : "\u25B8"}
                                </button>
                              </td>
                              <td className="py-2 pr-3">
                                <span className="font-semibold">{g.symbol}</span>
                                {/* The caption IS the control. An unlabelled chevron in a
                                    column with an sr-only header is not a discoverable
                                    escape hatch, and the CIO approved grouping ON
                                    CONDITION that splitting a row is reachable. */}
                                <div className="text-[10px]" style={muted}>
                                  <button
                                    type="button"
                                    aria-expanded={open}
                                    onClick={(e) => { e.stopPropagation(); toggleGroup(g.key); }}
                                    className="underline underline-offset-2"
                                    style={{ color: "inherit" }}
                                  >
                                    {g.fills.length === 1 ? "1 execution" : `${g.fills.length} executions`}
                                    {" — "}{open ? "hide" : "show"}
                                  </button>
                                  {some ? ` · ${picked.length} selected` : ""}
                                </div>
                              </td>
                              <td className="py-2 pr-3 font-semibold">{g.direction}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {g.totalQty.toLocaleString()}
                                <span className="ml-1 text-[10px]" style={muted}>
                                  {unitWord(g.secType, g.totalQty)}
                                </span>
                              </td>
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {px(g.avgPrice)}
                                {g.priceLo != null && g.priceHi != null && g.priceHi - g.priceLo > 0.005 ? (
                                  <div className="text-[10px]" style={muted}>
                                    {px(g.priceLo)}&ndash;{px(g.priceHi)}
                                  </div>
                                ) : null}
                              </td>
                              {/* Dollar size, because a trade is recognised by what it
                                  cost far faster than by a share count. */}
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {g.notional == null ? "—" : fmtUSD(g.notional)}
                              </td>
                              <td className="py-2 text-right tabular-nums" style={muted}>
                                {g.sessionDate} <span className="text-[10px]">ET</span>
                                {g.lastAt ? (
                                  <div className="text-[10px]">
                                    last fill {fmtLocalDateTime(g.lastAt).split(", ")[1] || ""} local
                                  </div>
                                ) : null}
                              </td>
                            </tr>

                            {open ? (
                              <tr style={{ backgroundColor: "var(--bg-secondary)" }}>
                                <td colSpan={8} className="px-4 py-2">
                                  <div className={MICRO_LABEL} style={muted}>
                                    The executions TWS split this order into
                                  </div>
                                  <p className="mt-1 mb-2 text-[11px] leading-4" style={muted}>
                                    All of these are recorded as one trade. Untick any that belong to a
                                    <strong> different decision</strong> — they stay in the queue as their own break.
                                  </p>
                                  <table className="w-full text-[11px]">
                                    <tbody>
                                      {g.fills.map((f) => (
                                        <tr key={f.fillId}>
                                          <td className="py-1 pr-3 w-6">
                                            <input
                                              type="checkbox"
                                              aria-label={`Include execution of ${f.qty} at ${f.price}`}
                                              disabled={queueUnavailable}
                                              checked={selectedFillIds.includes(f.fillId)}
                                              // SCOPED TO THIS TRADE. An unscoped append
                                              // broke the exclusivity the group row
                                              // enforces: two same-contract, same-side
                                              // trades from different sessions could both
                                              // be selected, and the endpoint -- which
                                              // checks only contract and direction --
                                              // would merge them into one ledger row,
                                              // dated to the older session, with a
                                              // quantity matching neither trade.
                                              onChange={(e) =>
                                                setSelectedFillIds((prev) => {
                                                  const mine = prev.filter((id) => ids.includes(id));
                                                  return e.target.checked
                                                    ? [...mine, f.fillId]
                                                    : mine.filter((id) => id !== f.fillId);
                                                })
                                              }
                                            />
                                          </td>
                                          <td className="py-1 pr-3 text-right tabular-nums">
                                            {(f.qty || 0).toLocaleString()}
                                          </td>
                                          <td className="py-1 pr-3 text-right tabular-nums">@ {px(f.price)}</td>
                                          <td className="py-1 pr-3 tabular-nums" style={muted}>
                                            {f.tradeTimeUtc ? fmtLocalDateTime(f.tradeTimeUtc) : "—"}
                                          </td>
                                          <td className="py-1 text-right text-[10px]" style={muted}>
                                            {f.execId || ""}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}

                      {/* Broker side 'X' is a transfer marker, not a directional trade.
                          The endpoint refuses it, so it must never sit in a group that
                          looks recordable. */}
                      {ungroupableFills.map((f) => (
                        <tr key={f.fillId} className="border-t" style={{ borderColor: "var(--card-border)" }}>
                          <td className="py-2 pr-2" />
                          <td className="py-2 pr-2" />
                          <td className="py-2 pr-3">{f.symbol || `conid ${f.contractId}`}</td>
                          <td className="py-2 pr-3" style={{ color: "rgb(var(--c-warn))" }}>
                            {f.side || "—"}
                            {/* A MISSING side is an open question, not a settled fact.
                                Rendering both as "not a directional trade" asserted
                                knowledge we do not have. */}
                            <div className="text-[10px]" style={muted}>
                              {f.side
                                ? "transfer / exchange marker — not a trade"
                                : "direction missing from the broker record"}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">{numOrDash(f.qty)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{px(f.price)}</td>
                          <td className="py-2 pr-3 text-right" style={muted}>—</td>
                          <td className="py-2 text-right tabular-nums" style={muted}>
                            {easternSession(f.tradeTimeUtc)} <span className="text-[10px]">ET</span>
                            {f.tradeTimeUtc ? (
                              <div className="text-[10px]">
                                {fmtLocalDateTime(f.tradeTimeUtc).split(", ")[1] || ""} local
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )}
              </SectionCard>
              </div>
            ) : null}

            <FillVsQuoteByBoard ledger={ledger} />

            <SectionCard
              title="Round-trips"
              subtitle="Each position from open to close, with the reason recorded at every step. Netted over the contract's full history, not the display window."
              right={
                ledger?.ledgerAvailable === false
                  ? <span className={chipClass("danger")}>ledger table missing</span>
                  : <span className={chipClass("neutral")}>{roundTrips.length}</span>
              }
            >
              {ledgerError ? (
                <div className="rounded-lg px-3 py-8 text-center text-[13px]" style={{ color: "rgb(var(--c-down))" }}>
                  Could not load the ledger — {ledgerError}
                </div>
              ) : ledger?.ledgerAvailable === false ? (
                <div className="rounded-lg px-3 py-8 text-center text-[13px]" style={{ color: "rgb(var(--c-warn))" }}>
                  {ledger?.note || "The ledger table does not exist yet."}
                </div>
              ) : ledgerLoading ? (
                /* "Empty" is a claim about the ledger; "not arrived" is a claim about the
                   network. Printing the first while the second is true asserts something
                   we have not checked. */
                <div className="rounded-lg px-3 py-10 text-center text-[13px]" style={muted}>
                  Reading the ledger…
                </div>
              ) : !roundTrips.length ? (
                <div className="rounded-lg px-3 py-10 text-center text-[13px]" style={muted}>
                  The ledger is empty. It starts from the first trade you record here.
                </div>
              ) : (
                <div className="space-y-2">
                  {roundTrips.map((t) => <RoundTripCard key={`${t.contractId}-${t.openedOn || t.closedOn || t.entryCount}`} trip={t} />)}
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* ============================= LIVE ============================= */}
          <TabsContent value="live">
            <SectionCard
              title="Live order &amp; fill mirror"
              subtitle="Read-only view of broker state for the fund sub-accounts. Places and cancels nothing. Every filled order is checked against the ledger; one the ledger does not hold starts a fill capture so its reason can be recorded."
              right={
                blotterHeldAt || capturing
                  ? <span className={chipClass("neutral")} title="A fill capture is reading IBKR's executions and holds the account selection. This blotter shows the last read and resumes within a minute.">Paused · fill capture reading IBKR</span>
                  : feedDown
                  ? <span className={chipClass("warning")} title={`Broker feed unavailable (${(liveOrders?.errors ?? []).join(", ") || "no fund sub-accounts"})`}>Feed degraded</span>
                  : <LiveBadge title="Read-only — refreshes every 12s while this tab is visible" />
              }
            >
              {(liveOrders?.working_orders?.length ?? 0) > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em]" style={muted}>
                        <th scope="col" className="pb-2 pr-3 font-semibold">Instrument</th>
                        <th scope="col" className="pb-2 pr-3 font-semibold">Side</th>
                        <th scope="col" className="pb-2 pr-3 font-semibold">Status</th>
                        <th scope="col" className="pb-2 pr-3 font-semibold text-right">Filled</th>
                        <th scope="col" className="pb-2 pr-3 font-semibold text-right">Avg fill</th>
                        <th scope="col" className="pb-2 pr-3 font-semibold text-right">Time</th>
                        <th scope="col" className="pb-2 font-semibold">Ledger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveOrders.working_orders.map((o: any, i: number) => {
                        const legs: any[] = Array.isArray(o.legs) ? o.legs : [];
                        const key = String(o.orderId ?? `${o.conid ?? "x"}-${o.side ?? "x"}-${o.symbol ?? i}`);
                        const isOpen = expandedOrders.has(key);
                        const fillKey = blotterFillKey(o);
                        const ledgerState = blotterRecon.byKey.get(fillKey) ?? null;
                        const autoTries = autoAttemptsRef.current[fillKey] || 0;
                        return (
                          <React.Fragment key={key}>
                            <tr className="border-t" style={{ borderColor: "var(--card-border)" }}>
                              <td className="py-2 pr-3">
                                {legs.length ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleOrder(key)}
                                    aria-expanded={isOpen}
                                    className="text-left font-semibold"
                                  >
                                    <span className="mr-1.5 text-[10px]" style={muted}>{isOpen ? "▾" : "▸"}</span>
                                    {o.symbol ?? "—"}
                                  </button>
                                ) : (
                                  <span className="font-semibold">{o.symbol ?? "—"}</span>
                                )}
                                <div className="text-[10px]" style={muted}>
                                  {o.orderType ?? "—"}{o.limitPrice != null ? ` · lmt ${px(o.limitPrice)}` : ""}
                                </div>
                              </td>
                              <td className="py-2 pr-3 font-semibold">{o.side ?? "—"}</td>
                              <td className="py-2 pr-3"><span className={chipClass(orderStatusChipTone(o.status))}>{o.status ?? "—"}</span></td>
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {numOrDash(o.filled)} <span style={muted}>/ {numOrDash(o.totalQty)}</span>
                              </td>
                              <td className="py-2 pr-3 text-right tabular-nums">{px(o.avgFillPrice)}</td>
                              <td className="py-2 pr-3 text-right text-[11px] tabular-nums" style={muted}>
                                {o.lastExecutionTime_r ? new Date(o.lastExecutionTime_r).toLocaleTimeString() : "—"}
                              </td>
                              <td className="py-2">
                                {/* Where this fill stands in the LEDGER. Nothing filled, nothing
                                    to say: a working order is not yet a trade. */}
                                {!ledgerState ? (
                                  <span className="text-[11px]" style={muted}>—</span>
                                ) : ledgerState.kind === "recorded" ? (
                                  <span className={chipClass("positive")}>Reason recorded</span>
                                ) : ledgerState.kind === "needs_reason" ? (
                                  <button
                                    type="button"
                                    className={chipClass("danger")}
                                    onClick={ledgerState.ticketOrderId != null ? () => setActiveTab("decide") : goToQueue}
                                    title={
                                      ledgerState.ticketOrderId != null
                                        ? "This trade has an open ticket. Record it there, on the Decide tab."
                                        : "Captured from IBKR with no reason. Record why in the Ledger queue."
                                    }
                                  >
                                    Needs a reason{ledgerState.ticketOrderId != null ? " (on its ticket)" : ""}
                                  </button>
                                ) : ledgerState.kind === "not_captured" ? (
                                  <div>
                                    <span className={chipClass("warning")}>Not in ledger yet</span>
                                    <div className="mt-0.5 text-[10px]" style={muted}>
                                      {capturing
                                        ? "reading IBKR’s execution feed…"
                                        : autoTries >= AUTO_CAPTURE_MAX_ATTEMPTS
                                          ? `capture tried ${autoTries}×; press Sync fills`
                                          : autoTries > 0
                                            ? `capture tried ${autoTries}×; retrying`
                                            : "capture queued"}
                                    </div>
                                  </div>
                                ) : (
                                  <span className={chipClass("neutral")} title="The ledger could not be read, so this fill cannot be checked.">
                                    Ledger unreadable
                                  </span>
                                )}
                              </td>
                            </tr>
                            {isOpen && legs.length
                              ? legs.map((l: any, li: number) => (
                                  <tr key={`${key}-leg-${l.executionId ?? li}`} style={{ borderColor: "var(--card-border)" }}>
                                    <td className="py-1 pr-3"><span className="ml-4 text-[11px]" style={muted}>└ fill {li + 1}</span></td>
                                    <td className="py-1 pr-3" />
                                    <td className="py-1 pr-3" />
                                    <td className="py-1 pr-3 text-right text-[11px] tabular-nums" style={muted}>{numOrDash(l.size)}</td>
                                    <td className="py-1 pr-3 text-right text-[11px] tabular-nums" style={muted}>{px(l.price)}</td>
                                    <td className="py-1 pr-3 text-right text-[11px] tabular-nums" style={muted}>{fmtTradeTimeGmt(l.tradeTime)}</td>
                                    <td className="py-1" />
                                  </tr>
                                ))
                              : null}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : feedDown ? (
                <div className="rounded-lg px-3 py-8 text-center text-[13px]" style={{ color: "rgb(var(--c-warn))" }}>
                  Broker feed unavailable — today&rsquo;s orders cannot be confirmed. Last good data{" "}
                  {liveOrders?.as_of_utc ? fmtLocalDateTime(liveOrders.as_of_utc) : "—"}.
                </div>
              ) : (
                <div className="rounded-lg px-3 py-10 text-center text-[13px]" style={muted}>No broker orders today.</div>
              )}
              <div className="mt-3 text-[11px]" style={muted}>
                IBKR · {liveOrders?.as_of_utc ? fmtLocalDateTime(liveOrders.as_of_utc) : "—"}
              </div>
            </SectionCard>
          </TabsContent>
        </Tabs>

        {/* ---- governance ---- */}
        <Card style={{ borderColor: "rgb(var(--c-accent) / 0.45)" }}>
          <div className={MICRO_LABEL} style={muted}>How this desk works</div>
          <div className="mt-2 space-y-1 text-[12px]" style={secondary}>
            <div>• No order is transmitted from this page. All execution is manual, in IBKR TWS.</div>
            <div>• Recording a trade writes a ledger entry attributed to you, plus the compliance audit trail.</div>
            <div>• Fill price and quantity come from the broker&rsquo;s execution feed — never typed, never assumed.</div>
            <div>• A recommendation you decline is recorded too. Choosing not to trade is part of the record.</div>
          </div>
        </Card>
      </div>

      {claiming && selectedFillIds.length ? (
        <ClaimFillsModal
          fills={unmatchedFills.filter((f) => selectedFillIds.includes(f.fillId))}
          // Resolved by CONTAINMENT: the group that holds EVERY selected fill. `.some`
          // returned the first group holding ANY of them, so a selection spanning two
          // trades resolved to a strict subset -- and the completeness line then
          // cheerfully reported that subset as complete.
          //
          // null when no single group owns the whole selection, which the modal treats
          // as a hard refusal rather than a missing nicety.
          group={
            fillGroups.find((g) =>
              selectedFillIds.every((id) => g.fills.some((f) => f.fillId === id))
            ) || null
          }
          // True when the queue is showing only part of the backlog: a group built from
          // a truncated payload may be a fragment, so it must not be called complete.
          truncated={truncated}
          queueUnavailable={queueUnavailable}
          busy={claimBusy}
          actor={userEmail}
          lotFlag={claimLotFlag}
          onCancel={() => { setClaiming(false); setClaimLotFlag(null); }}
          onConfirm={recordDiscretionary}
        />
      ) : null}

      {bulk ? (
        <BulkDecisionModal
          decision={bulk.decision}
          tickets={bulk.tickets}
          actor={userEmail || null}
          run={bulkRun}
          onConfirm={(why, revisitOn) => {
            const { eligible, excluded } = splitForBulk(bulk.tickets, bulk.decision);
            runBulk(bulk.decision, eligible, why, revisitOn, excluded.length);
          }}
          onStop={() => { bulkStopRef.current = true; }}
          onClose={() => { setBulk(null); setBulkRun(null); }}
        />
      ) : null}

      {pending ? (
        <DecisionModal
          order={pending.order}
          decision={pending.decision}
          busy={busyId === pending.order.orderId}
          actor={userEmail || null}
          lotFlag={decisionLotFlag && decisionLotFlag.orderId === pending.order.orderId ? decisionLotFlag.flag : null}
          onConfirm={(why, actuals) => recordDecision(pending.order, pending.decision, why, actuals)}
          onCancel={() => { setPending(null); setDecisionLotFlag(null); }}
        />
      ) : null}
    </Layout>
  );
};

export default TradingDesk;
